import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDate as formatDateUtil, formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { 
  BarChart3, 
  Users, 
  FileText, 
  Shield,
  Download,
  Loader2,
  TrendingUp,
  Building,
  Lock,
  Calendar,
  RefreshCw,
  ClipboardList,
  Filter,
  Eye,
  Settings2,
  X,
  MapPin,
  ArrowRight,
  ArrowLeft,
  Target,
  FileDown,
  Briefcase,
  ChevronDown,
  ChevronUp,
  UserCheck,
  Activity,
  Heart,
  DollarSign,
  Receipt,
  Check,
  UserCog,
  BookOpen,
  GraduationCap,
  Clock,
  LifeBuoy,
  Flag
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { FLAG_MANAGEMENT_ROLES } from '../components/caseload-flags/flagUtils';
import { Link } from 'react-router-dom';
import { formatTime12 } from '../utils/timeZone';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#92298d', '#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6', '#ec4899'];

// Helper function to calculate fiscal year dates (Oct 1 - Sep 30)
const getFiscalYearDates = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); // 0-indexed (0 = January, 9 = October)
  
  let fiscalYearStart, fiscalYearEnd;
  
  // If current date is before October 1 (month < 9), fiscal year started last year
  // If current date is on or after October 1 (month >= 9), fiscal year started this year
  if (currentMonth < 9) {
    // Fiscal year: Oct 1 of previous year to Sep 30 of current year
    fiscalYearStart = `${currentYear - 1}-10-01`;
    fiscalYearEnd = `${currentYear}-09-30`;
  } else {
    // Fiscal year: Oct 1 of current year to Sep 30 of next year
    fiscalYearStart = `${currentYear}-10-01`;
    fiscalYearEnd = `${currentYear + 1}-09-30`;
  }
  
  return { start_date: fiscalYearStart, end_date: fiscalYearEnd };
};

const ReportsPage = ({ reportType }) => {
  const { user } = useAuth();
  
  // Role-based permissions - defined early so they can be used in effects/handlers
  const canViewSnapEtCaseload = user?.role && ['system_admin', 'executive', 'operations_manager'].includes(user.role);
  const canViewSnapOutreach = user?.role && ['system_admin', 'executive', 'operations_manager'].includes(user.role);
  
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState(reportType || 'enrollments');
  const [dateRange, setDateRange] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  
  // Flag to determine if we're viewing a dedicated report page
  const isDedicatedReportPage = !!reportType;
  
  // Sync activeTab with reportType prop when navigating to dedicated pages
  useEffect(() => {
    if (reportType) {
      setActiveTab(reportType);
    }
  }, [reportType]);
  
  // Report data
  const [caseloadData, setCaseloadData] = useState(null);
  const [participantsData, setParticipantsData] = useState(null);
  const [contractsData, setContractsData] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [enrollmentsData, setEnrollmentsData] = useState(null);
  const [snapCertExpirationsData, setSnapCertExpirationsData] = useState(null);
  const [snapEnrollmentExpirationsData, setSnapEnrollmentExpirationsData] = useState(null);
  const [snapDaysAhead, setSnapDaysAhead] = useState(30);
  const [snapEtCaseloadData, setSnapEtCaseloadData] = useState(null);
  const [snapEtCaseloadLoading, setSnapEtCaseloadLoading] = useState(false);
  const [expandedCaseManagers, setExpandedCaseManagers] = useState({});
  const [snapEtFilters, setSnapEtFilters] = useState({
    start_date: '',
    end_date: '',
    office_location: '_all',
    program_type: '_all',
    contract_id: '_all',
    state: '_all',
    case_manager_id: '_all',
    case_status: '_all'
  });
  
  // Reassign Case state
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassigningCase, setReassigningCase] = useState(null);
  const [reassignTargetCM, setReassignTargetCM] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [reassignLoading, setReassignLoading] = useState(false);
  const [availableCaseManagers, setAvailableCaseManagers] = useState([]);
  
  // SNAP Outreach Report state
  const [snapOutreachData, setSnapOutreachData] = useState(null);
  const [snapOutreachLoading, setSnapOutreachLoading] = useState(false);
  const [snapOutreachExportingPdf, setSnapOutreachExportingPdf] = useState(false);
  const [snapOutreachExportingExcel, setSnapOutreachExportingExcel] = useState(false);
  const [snapOutreachFilters, setSnapOutreachFilters] = useState(() => {
    // Default to current fiscal year (Oct 1 - Sep 30)
    const fiscalYear = getFiscalYearDates();
    return {
      start_date: fiscalYear.start_date,
      end_date: fiscalYear.end_date,
      current_month: false,
      current_fiscal_year: true,
      state: '_all',
      enrollment_type_id: '_all'
    };
  });
  const [snapOutreachEnrollmentTypes, setSnapOutreachEnrollmentTypes] = useState([]);
  
  // SNAP Outreach Enrollment List state
  const [snapEnrollmentListData, setSnapEnrollmentListData] = useState(null);
  const [snapEnrollmentListLoading, setSnapEnrollmentListLoading] = useState(false);
  const [snapEnrollmentListFilters, setSnapEnrollmentListFilters] = useState(() => {
    const fiscalYear = getFiscalYearDates();
    return {
      start_date: fiscalYear.start_date,
      end_date: fiscalYear.end_date,
      current_month: false,
      current_fiscal_year: true,
      state: '_all'
    };
  });
  
  // Pathways Module Completion Report state
  const [pathwaysData, setPathwaysData] = useState(null);
  const [pathwaysLoading, setPathwaysLoading] = useState(false);
  const [pathwaysFilters, setPathwaysFilters] = useState({
    start_date: '',
    end_date: ''
  });
  const [expandedModules, setExpandedModules] = useState({});
  
  // HELP Institute Course Completion Report state
  const [helpInstituteData, setHelpInstituteData] = useState(null);
  const [helpInstituteLoading, setHelpInstituteLoading] = useState(false);
  const [helpInstituteFilters, setHelpInstituteFilters] = useState({
    start_date: '',
    end_date: ''
  });
  const [expandedCourses, setExpandedCourses] = useState({});
  
  // Participant Costs Report state
  const [participantCostsData, setParticipantCostsData] = useState(null);
  const [participantCostsLoading, setParticipantCostsLoading] = useState(false);
  const [participantCostsFilters, setParticipantCostsFilters] = useState({
    start_date: '',
    end_date: '',
    enrollment_type_id: '_all',
    state: '_all',
    category_id: '_all'
  });
  const [expandedCostCategories, setExpandedCostCategories] = useState({});
  const [showItemizedTransactions, setShowItemizedTransactions] = useState(true);
  
  // Employment Outcomes Report state
  const [employmentOutcomesData, setEmploymentOutcomesData] = useState(null);
  const [employmentOutcomesLoading, setEmploymentOutcomesLoading] = useState(false);
  const [employmentOutcomesFilters, setEmploymentOutcomesFilters] = useState({
    start_date: '',
    end_date: '',
    employment_status_at_enrollment: '_all',
    status: '_all',
    case_manager_id: '_all',
    states: []  // Array of selected states
  });
  const [employmentOutcomesSortConfig, setEmploymentOutcomesSortConfig] = useState({
    key: 'start_date',
    direction: 'desc'
  });
  
  // Retention Report state
  const [retentionData, setRetentionData] = useState(null);
  const [retentionLoading, setRetentionLoading] = useState(false);
  const [retentionFilters, setRetentionFilters] = useState({
    start_date: '',
    end_date: '',
    states: []  // Array of selected states
  });
  const [retentionSortConfig, setRetentionSortConfig] = useState({
    key: 'retention_start_date',
    direction: 'desc'
  });
  
  // SNAP E&T Enrollment Summary Report state
  const [enrollmentSummaryData, setEnrollmentSummaryData] = useState(null);
  const [enrollmentSummaryLoading, setEnrollmentSummaryLoading] = useState(false);
  const [enrollmentSummaryExportingPdf, setEnrollmentSummaryExportingPdf] = useState(false);
  const [enrollmentSummaryExportingExcel, setEnrollmentSummaryExportingExcel] = useState(false);
  const [enrollmentSummaryFilters, setEnrollmentSummaryFilters] = useState(() => {
    const fiscalYear = getFiscalYearDates();
    return {
      start_date: fiscalYear.start_date,
      end_date: fiscalYear.end_date,
      current_month: false,
      current_fiscal_year: true,
      states: []
    };
  });

  // SNAP E&T Encounters Report state
  const [encountersReportData, setEncountersReportData] = useState(null);
  const [encountersReportLoading, setEncountersReportLoading] = useState(false);
  const [encountersReportMonth, setEncountersReportMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  
  // Master Enrollment Metrics Report state
  const [masterEnrollmentData, setMasterEnrollmentData] = useState(null);
  const [masterEnrollmentLoading, setMasterEnrollmentLoading] = useState(false);
  const [fiscalGoals, setFiscalGoals] = useState({});
  const [currentFiscalGoal, setCurrentFiscalGoal] = useState(0);
  const [masterEnrollmentFilters, setMasterEnrollmentFilters] = useState(() => {
    // Default to current fiscal year (Oct 1 - Sep 30)
    const today = new Date();
    const currentYear = today.getFullYear();
    let startDate, endDate;
    
    if (today.getMonth() >= 9) { // October or later (month is 0-indexed)
      startDate = `${currentYear}-10-01`;
      endDate = `${currentYear + 1}-09-30`;
    } else {
      startDate = `${currentYear - 1}-10-01`;
      endDate = `${currentYear}-09-30`;
    }
    
    return {
      start_date: startDate,
      end_date: endDate,
      states: []
    };
  });
  const [masterEnrollmentSortConfig, setMasterEnrollmentSortConfig] = useState({
    key: 'enrollment_date',
    direction: 'desc'
  });
  
  // Caseload Review Report state
  const [caseloadReviewData, setCaseloadReviewData] = useState(null);
  const [caseloadReviewLoading, setCaseloadReviewLoading] = useState(false);
  const [caseloadReviewFilters, setCaseloadReviewFilters] = useState({
    program_type: '_all',
    week_of: '_all',
    case_manager_id: '_all',
    review_status: '_all',
    current_week: false
  });
  const [caseloadReviewFilterOptions, setCaseloadReviewFilterOptions] = useState({
    programs: [],
    case_managers: [],
    weeks: [],
    statuses: []
  });
  const [expandedCaseloadReviews, setExpandedCaseloadReviews] = useState({});
  const [caseloadReviewDetails, setCaseloadReviewDetails] = useState({});
  
  // Community Service Report state
  const [communityServiceData, setCommunityServiceData] = useState(null);
  const [communityServiceLoading, setCommunityServiceLoading] = useState(false);
  const [communityServiceFilters, setCommunityServiceFilters] = useState({
    year: new Date().getFullYear(),
    location: '_all'
  });
  const [communityServiceSortConfig, setCommunityServiceSortConfig] = useState({
    key: 'participant_name',
    direction: 'asc'
  });
  
  // Enrollments filters
  const [filterOptions, setFilterOptions] = useState(null);
  const [enrollmentFilters, setEnrollmentFilters] = useState({
    program_type: '_all',
    state: '_all',
    contract: '_all',
    status: 'all',
    case_manager_id: '_all',
    county: '_all'
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Custom report builder state
  const [showCustomReport, setShowCustomReport] = useState(false);
  const [customReportData, setCustomReportData] = useState(null);
  const [customReportLoading, setCustomReportLoading] = useState(false);
  const [enrollmentTypes, setEnrollmentTypes] = useState([]);
  const [customReportConfig, setCustomReportConfig] = useState({
    entity_type: 'enrollments',
    fields: [],
    filters: {
      enrollment_type: '_all',
      start_date: '',
      end_date: ''
    },
    sort_by: 'start_date',
    sort_order: 'desc',
    limit: 500
  });

  useEffect(() => {
    fetchSummary();
    fetchFilterOptions();
    fetchEnrollmentTypes();
  }, []);

  useEffect(() => {
    if (activeTab === 'caseload') fetchCaseloadReport();
    else if (activeTab === 'participants') fetchParticipantsReport();
    else if (activeTab === 'contracts') fetchContractsReport();
    else if (activeTab === 'audit') fetchAuditReport();
    else if (activeTab === 'enrollments') fetchEnrollmentsReport();
    else if (activeTab === 'snap-cert-expirations') fetchSnapCertExpirationsReport();
    else if (activeTab === 'snap-enrollment-expirations') fetchSnapEnrollmentExpirationsReport();
    else if (activeTab === 'snap-et-caseload') fetchSnapEtCaseloadReport();
    else if (activeTab === 'snap-outreach') fetchSnapOutreachReport();
    else if (activeTab === 'pathways-modules') {} // Manual fetch only
    else if (activeTab === 'help-institute-courses') {} // Manual fetch only
    else if (activeTab === 'caseload-review') fetchCaseloadReviewReport();
    else if (activeTab === 'snap-et-encounters') fetchEncountersReport();
  }, [activeTab, startDate, endDate, snapDaysAhead]);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/reports/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/reports/filter-options`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const fetchEnrollmentTypes = async () => {
    try {
      const response = await axios.get(`${API}/enrollment-types`);
      setEnrollmentTypes(response.data.enrollment_types || []);
    } catch (error) {
      console.error('Failed to fetch enrollment types:', error);
    }
  };

  const fetchCaseloadReport = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const response = await axios.get(`${API}/reports/caseload?${params}`);
      setCaseloadData(response.data);
    } catch (error) {
      toast.error('Failed to load caseload report');
    }
  };

  const fetchParticipantsReport = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const response = await axios.get(`${API}/reports/participants?${params}`);
      setParticipantsData(response.data);
    } catch (error) {
      toast.error('Failed to load participants report');
    }
  };

  const fetchContractsReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/contracts`);
      setContractsData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        // Access denied - handled in UI
      } else {
        toast.error('Failed to load contracts report');
      }
    }
  };

  const fetchAuditReport = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const response = await axios.get(`${API}/reports/audit?${params}`);
      setAuditData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        // Access denied - handled in UI
      } else {
        toast.error('Failed to load audit report');
      }
    }
  };

  const fetchEnrollmentsReport = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (enrollmentFilters.program_type && enrollmentFilters.program_type !== '_all') params.append('program_type', enrollmentFilters.program_type);
      if (enrollmentFilters.state && enrollmentFilters.state !== '_all') params.append('state', enrollmentFilters.state);
      if (enrollmentFilters.contract && enrollmentFilters.contract !== '_all') params.append('contract', enrollmentFilters.contract);
      if (enrollmentFilters.status && enrollmentFilters.status !== 'all') params.append('status', enrollmentFilters.status);
      if (enrollmentFilters.case_manager_id && enrollmentFilters.case_manager_id !== '_all') params.append('case_manager_id', enrollmentFilters.case_manager_id);
      if (enrollmentFilters.county && enrollmentFilters.county !== '_all') params.append('county', enrollmentFilters.county);
      
      const response = await axios.get(`${API}/reports/enrollments?${params}`);
      setEnrollmentsData(response.data);
    } catch (error) {
      toast.error('Failed to load enrollments report');
    }
  };

  const fetchSnapCertExpirationsReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/snap-et-cert-expirations?days_ahead=${snapDaysAhead}`);
      setSnapCertExpirationsData(response.data);
    } catch (error) {
      toast.error('Failed to load SNAP E&T certification expirations report');
    }
  };

  const fetchSnapEnrollmentExpirationsReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/snap-et-enrollment-expirations?days_ahead=${snapDaysAhead}`);
      setSnapEnrollmentExpirationsData(response.data);
    } catch (error) {
      toast.error('Failed to load SNAP E&T enrollment expirations report');
    }
  };

  const fetchSnapEtCaseloadReport = async () => {
    setSnapEtCaseloadLoading(true);
    try {
      const params = new URLSearchParams();
      if (snapEtFilters.start_date) params.append('start_date', snapEtFilters.start_date);
      if (snapEtFilters.end_date) params.append('end_date', snapEtFilters.end_date);
      if (snapEtFilters.office_location && snapEtFilters.office_location !== '_all') params.append('office_location', snapEtFilters.office_location);
      if (snapEtFilters.program_type && snapEtFilters.program_type !== '_all') params.append('program_type', snapEtFilters.program_type);
      if (snapEtFilters.contract_id && snapEtFilters.contract_id !== '_all') params.append('contract_id', snapEtFilters.contract_id);
      if (snapEtFilters.state && snapEtFilters.state !== '_all') params.append('state', snapEtFilters.state);
      if (snapEtFilters.case_manager_id && snapEtFilters.case_manager_id !== '_all') params.append('case_manager_id', snapEtFilters.case_manager_id);
      if (snapEtFilters.case_status && snapEtFilters.case_status !== '_all') params.append('case_status', snapEtFilters.case_status);
      
      const response = await axios.get(`${API}/reports/snap-et-caseload-management?${params}`);
      setSnapEtCaseloadData(response.data);
    } catch (error) {
      console.error('Failed to load SNAP E&T Caseload report:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view this report');
      } else {
        toast.error('Failed to load SNAP E&T Caseload report');
      }
    } finally {
      setSnapEtCaseloadLoading(false);
    }
  };

  const exportSnapEtCaseloadCsv = () => {
    if (!snapEtCaseloadData) return;
    
    const headers = ['Case Manager', 'Office', 'Job Title', 'Active Participants', 'Open Cases', 'Closed (Period)', '% of Total', 'Last Activity'];
    const rows = snapEtCaseloadData.case_managers.map(cm => [
      cm.name,
      cm.office_location,
      cm.job_title,
      cm.total_active_participants,
      cm.open_cases,
      cm.closed_cases_period,
      `${cm.percentage_of_total}%`,
      cm.last_participant_activity
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `SNAP_ET_Caseload_Management_Report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('SNAP E&T Caseload report exported');
  };

  // Fetch SNAP E&T Encounters Report
  const fetchEncountersReport = async () => {
    setEncountersReportLoading(true);
    try {
      const response = await axios.get(`${API}/reports/snap-et-encounters?month=${encountersReportMonth}`);
      setEncountersReportData(response.data);
    } catch (error) {
      console.error('Failed to load SNAP E&T Encounters report:', error);
      toast.error('Failed to load SNAP E&T Encounters report');
    } finally {
      setEncountersReportLoading(false);
    }
  };

  // Export Encounters Report to CSV
  const exportEncountersReportCsv = () => {
    if (!encountersReportData || !encountersReportData.programs?.length) {
      toast.error('No data to export');
      return;
    }
    
    const headers = ['Program', 'Participant List', 'Case Manager', 'Case Status', 'Encounter 1', 'Encounter 2', 'Encounter 3', 'Encounter 4', 'Encounter 5'];
    const rows = [];
    
    for (const program of encountersReportData.programs) {
      for (const p of program.participants) {
        rows.push([
          program.program_label,
          p.full_name,
          p.case_manager_name,
          p.case_status === 'Closed' ? `Closed ${p.case_closed_date || ''}`.trim() : (p.case_status || ''),
          p.encounter_1 || '',
          p.encounter_2 || '',
          p.encounter_3 || '',
          p.encounter_4 || '',
          p.encounter_5 || ''
        ]);
      }
    }
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `snap_et_encounters_${encountersReportMonth}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Report exported');
  };

  const toggleCaseManagerExpanded = (cmId) => {
    setExpandedCaseManagers(prev => ({
      ...prev,
      [cmId]: !prev[cmId]
    }));
  };

  // Reassign Case Functions
  const fetchAvailableCaseManagers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      const users = response.data.users || response.data || [];
      // Filter to case managers and management roles who can be assigned cases
      const eligibleUsers = users.filter(u => 
        u.is_active !== false && 
        ['case_manager', 'case_management_supervisor', 'system_admin', 'executive', 'operations_manager'].includes(u.role)
      );
      setAvailableCaseManagers(eligibleUsers);
    } catch (error) {
      console.error('Failed to fetch case managers:', error);
      toast.error('Failed to load case managers');
    }
  };

  const openReassignDialog = (caseItem, currentCmName) => {
    setReassigningCase({ ...caseItem, current_cm_name: currentCmName });
    setReassignTargetCM('');
    setReassignReason('');
    fetchAvailableCaseManagers();
    setReassignDialogOpen(true);
  };

  const handleReassignCase = async () => {
    if (!reassigningCase || !reassignTargetCM) {
      toast.error('Please select a Case Manager');
      return;
    }
    
    setReassignLoading(true);
    try {
      await axios.post(`${API}/cases/${reassigningCase.case_id}/reassign?new_staff_id=${reassignTargetCM}&reason=${encodeURIComponent(reassignReason)}`);
      
      const newCM = availableCaseManagers.find(cm => cm.id === reassignTargetCM);
      toast.success(`Case reassigned to ${newCM?.name || 'new Case Manager'}`);
      
      // Close dialog and refresh report
      setReassignDialogOpen(false);
      setReassigningCase(null);
      fetchSnapEtCaseloadReport();
    } catch (error) {
      console.error('Failed to reassign case:', error);
      toast.error(error.response?.data?.detail || 'Failed to reassign case');
    } finally {
      setReassignLoading(false);
    }
  };

  const fetchSnapOutreachReport = async () => {
    if (!canViewSnapOutreach) return;
    setSnapOutreachLoading(true);
    try {
      const params = new URLSearchParams();
      if (snapOutreachFilters.current_month) {
        params.append('current_month', 'true');
      } else {
        if (snapOutreachFilters.start_date) params.append('start_date', snapOutreachFilters.start_date);
        if (snapOutreachFilters.end_date) params.append('end_date', snapOutreachFilters.end_date);
      }
      if (snapOutreachFilters.state && snapOutreachFilters.state !== '_all') {
        params.append('state', snapOutreachFilters.state);
      }
      if (snapOutreachFilters.enrollment_type_id && snapOutreachFilters.enrollment_type_id !== '_all') {
        params.append('enrollment_type_id', snapOutreachFilters.enrollment_type_id);
      }
      
      const response = await axios.get(`${API}/reports/snap-outreach?${params}`);
      setSnapOutreachData(response.data);
      
      // Update available enrollment types from response
      if (response.data.filter_options?.enrollment_types) {
        setSnapOutreachEnrollmentTypes(response.data.filter_options.enrollment_types);
      }
    } catch (error) {
      console.error('Failed to load SNAP Outreach report:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view this report');
      } else if (error.response?.status === 400) {
        // Don't show error for missing date parameters - user hasn't selected dates yet
        setSnapOutreachData(null);
      } else {
        toast.error('Failed to load SNAP Outreach report');
      }
    } finally {
      setSnapOutreachLoading(false);
    }
  };

  const handleSnapOutreachCurrentMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setSnapOutreachFilters({
      ...snapOutreachFilters,
      start_date: firstDay.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0],
      current_month: true,
      current_fiscal_year: false
    });
  };

  const handleSnapOutreachCurrentFiscalYear = () => {
    const fiscalYear = getFiscalYearDates();
    setSnapOutreachFilters({
      ...snapOutreachFilters,
      start_date: fiscalYear.start_date,
      end_date: fiscalYear.end_date,
      current_month: false,
      current_fiscal_year: true
    });
  };

  // SNAP E&T Enrollment Summary handlers
  const handleEnrollmentSummaryCurrentMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setEnrollmentSummaryFilters({
      ...enrollmentSummaryFilters,
      start_date: firstDay.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0],
      current_month: true,
      current_fiscal_year: false
    });
  };

  const handleEnrollmentSummaryCurrentFiscalYear = () => {
    const fiscalYear = getFiscalYearDates();
    setEnrollmentSummaryFilters({
      ...enrollmentSummaryFilters,
      start_date: fiscalYear.start_date,
      end_date: fiscalYear.end_date,
      current_month: false,
      current_fiscal_year: true
    });
  };

  const fetchEnrollmentSummaryReport = async () => {
    setEnrollmentSummaryLoading(true);
    try {
      const params = new URLSearchParams();
      if (enrollmentSummaryFilters.start_date) params.append('start_date', enrollmentSummaryFilters.start_date);
      if (enrollmentSummaryFilters.end_date) params.append('end_date', enrollmentSummaryFilters.end_date);
      if (enrollmentSummaryFilters.states.length > 0) params.append('states', enrollmentSummaryFilters.states.join(','));
      
      const response = await axios.get(`${API}/reports/snap-et-enrollment-summary?${params}`);
      setEnrollmentSummaryData(response.data);
    } catch (error) {
      console.error('Failed to fetch Enrollment Summary report:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. You do not have permission to view this report.');
      } else {
        toast.error('Failed to load Enrollment Summary report');
      }
    } finally {
      setEnrollmentSummaryLoading(false);
    }
  };

  const handleEnrollmentSummaryExportPdf = async () => {
    setEnrollmentSummaryExportingPdf(true);
    try {
      const response = await axios.post(
        `${API}/reports/snap-et-enrollment-summary/export-pdf`,
        {
          start_date: enrollmentSummaryFilters.start_date,
          end_date: enrollmentSummaryFilters.end_date,
          states: enrollmentSummaryFilters.states.join(',')
        },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `snap_et_enrollment_summary_${enrollmentSummaryFilters.start_date}_${enrollmentSummaryFilters.end_date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('Failed to export PDF:', error);
      toast.error('Failed to export PDF');
    } finally {
      setEnrollmentSummaryExportingPdf(false);
    }
  };

  const handleEnrollmentSummaryExportExcel = async () => {
    setEnrollmentSummaryExportingExcel(true);
    try {
      const response = await axios.post(
        `${API}/reports/snap-et-enrollment-summary/export-excel`,
        {
          start_date: enrollmentSummaryFilters.start_date,
          end_date: enrollmentSummaryFilters.end_date,
          states: enrollmentSummaryFilters.states.join(',')
        },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `snap_et_enrollment_summary_${enrollmentSummaryFilters.start_date}_${enrollmentSummaryFilters.end_date}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel exported successfully');
    } catch (error) {
      console.error('Failed to export Excel:', error);
      toast.error('Failed to export Excel');
    } finally {
      setEnrollmentSummaryExportingExcel(false);
    }
  };

  const handleSnapOutreachGenerateReport = () => {
    fetchSnapOutreachReport();
  };

  const handleSnapOutreachExportPdf = async () => {
    setSnapOutreachExportingPdf(true);
    try {
      const response = await axios.post(
        `${API}/reports/snap-outreach/export-pdf`,
        {
          start_date: snapOutreachFilters.start_date,
          end_date: snapOutreachFilters.end_date,
          state: snapOutreachFilters.state,
          enrollment_type_id: snapOutreachFilters.enrollment_type_id
        },
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `snap_outreach_report_${snapOutreachFilters.start_date}_${snapOutreachFilters.end_date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('Failed to export PDF:', error);
      toast.error('Failed to export PDF');
    } finally {
      setSnapOutreachExportingPdf(false);
    }
  };

  const handleSnapOutreachExportExcel = async () => {
    setSnapOutreachExportingExcel(true);
    try {
      const response = await axios.post(
        `${API}/reports/snap-outreach/export-excel`,
        {
          start_date: snapOutreachFilters.start_date,
          end_date: snapOutreachFilters.end_date,
          state: snapOutreachFilters.state,
          enrollment_type_id: snapOutreachFilters.enrollment_type_id
        },
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `snap_outreach_report_${snapOutreachFilters.start_date}_${snapOutreachFilters.end_date}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel file exported successfully');
    } catch (error) {
      console.error('Failed to export Excel:', error);
      toast.error('Failed to export Excel file');
    } finally {
      setSnapOutreachExportingExcel(false);
    }
  };

  // SNAP Outreach Enrollment List functions
  const fetchSnapEnrollmentList = async () => {
    if (!snapEnrollmentListFilters.start_date || !snapEnrollmentListFilters.end_date) {
      toast.error('Please select both start and end dates');
      return;
    }
    setSnapEnrollmentListLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('start_date', snapEnrollmentListFilters.start_date);
      params.append('end_date', snapEnrollmentListFilters.end_date);
      if (snapEnrollmentListFilters.state && snapEnrollmentListFilters.state !== '_all') {
        params.append('state', snapEnrollmentListFilters.state);
      }
      
      const response = await axios.get(`${API}/reports/snap-outreach-enrollment-list?${params}`);
      setSnapEnrollmentListData(response.data);
    } catch (error) {
      console.error('Failed to fetch SNAP Outreach Enrollment List:', error);
      toast.error('Failed to fetch SNAP Outreach Enrollment List');
      setSnapEnrollmentListData(null);
    } finally {
      setSnapEnrollmentListLoading(false);
    }
  };

  const handleSnapEnrollmentListCurrentMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setSnapEnrollmentListFilters({
      ...snapEnrollmentListFilters,
      start_date: firstDay.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0],
      current_month: true,
      current_fiscal_year: false
    });
  };

  const handleSnapEnrollmentListCurrentFiscalYear = () => {
    const fiscalYear = getFiscalYearDates();
    setSnapEnrollmentListFilters({
      ...snapEnrollmentListFilters,
      start_date: fiscalYear.start_date,
      end_date: fiscalYear.end_date,
      current_month: false,
      current_fiscal_year: true
    });
  };

  // Pathways Module Completion Report functions
  const fetchPathwaysModuleReport = async () => {
    if (!pathwaysFilters.start_date || !pathwaysFilters.end_date) {
      toast.error('Please select both start and end dates');
      return;
    }
    setPathwaysLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('start_date', pathwaysFilters.start_date);
      params.append('end_date', pathwaysFilters.end_date);
      
      const response = await axios.get(`${API}/reports/pathways-module-completion?${params}`);
      setPathwaysData(response.data);
    } catch (error) {
      console.error('Failed to load Pathways Module Completion report:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view this report');
      } else {
        toast.error('Failed to load Pathways Module Completion report');
      }
    } finally {
      setPathwaysLoading(false);
    }
  };

  const toggleModuleExpanded = (moduleName) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleName]: !prev[moduleName]
    }));
  };

  const exportPathwaysReportCsv = () => {
    if (!pathwaysData) return;
    
    const headers = ['Module', 'Participant Name', 'Completion Date', 'Case ID'];
    const rows = [];
    
    Object.entries(pathwaysData.modules).forEach(([moduleName, moduleData]) => {
      moduleData.completions.forEach(completion => {
        rows.push([
          moduleName,
          completion.participant_name,
          completion.completion_date,
          completion.case_id
        ]);
      });
    });
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Pathways_Module_Completion_Report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Pathways Module Completion report exported');
  };

  // HELP Institute Course Completion Report functions
  const fetchHelpInstituteReport = async () => {
    if (!helpInstituteFilters.start_date || !helpInstituteFilters.end_date) {
      toast.error('Please select both start and end dates');
      return;
    }
    setHelpInstituteLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('start_date', helpInstituteFilters.start_date);
      params.append('end_date', helpInstituteFilters.end_date);
      
      const response = await axios.get(`${API}/reports/help-institute-course-completion?${params}`);
      setHelpInstituteData(response.data);
    } catch (error) {
      console.error('Failed to load HELP Institute Course Completion report:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view this report');
      } else {
        toast.error('Failed to load HELP Institute Course Completion report');
      }
    } finally {
      setHelpInstituteLoading(false);
    }
  };

  const toggleCourseExpanded = (courseName) => {
    setExpandedCourses(prev => ({
      ...prev,
      [courseName]: !prev[courseName]
    }));
  };

  const exportHelpInstituteReportCsv = () => {
    if (!helpInstituteData) return;
    
    const headers = ['Course', 'Participant Name', 'Completion Date', 'Case ID'];
    const rows = [];
    
    Object.entries(helpInstituteData.courses).forEach(([courseName, courseData]) => {
      courseData.completions.forEach(completion => {
        rows.push([
          courseName,
          completion.participant_name,
          completion.completion_date,
          completion.case_id
        ]);
      });
    });
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `HELP_Institute_Course_Completion_Report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('HELP Institute Course Completion report exported');
  };

  // Caseload Review Report functions
  const fetchCaseloadReviewReport = async () => {
    setCaseloadReviewLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (caseloadReviewFilters.program_type !== '_all') {
        params.append('program_type', caseloadReviewFilters.program_type);
      }
      if (caseloadReviewFilters.week_of !== '_all') {
        params.append('week_of', caseloadReviewFilters.week_of);
      }
      if (caseloadReviewFilters.case_manager_id !== '_all') {
        params.append('case_manager_id', caseloadReviewFilters.case_manager_id);
      }
      if (caseloadReviewFilters.review_status !== '_all') {
        params.append('review_status', caseloadReviewFilters.review_status);
      }
      if (caseloadReviewFilters.current_week) {
        params.append('current_week', 'true');
      }
      
      const response = await axios.get(`${API}/reports/caseload-review?${params}`);
      setCaseloadReviewData(response.data);
      setCaseloadReviewFilterOptions(response.data.filter_options || {
        programs: [],
        case_managers: [],
        weeks: [],
        statuses: []
      });
    } catch (error) {
      console.error('Failed to load Caseload Review report:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view this report');
      } else {
        toast.error('Failed to load Caseload Review report');
      }
    } finally {
      setCaseloadReviewLoading(false);
    }
  };

  const fetchCaseloadReviewDetails = async (headerId) => {
    try {
      const response = await axios.get(`${API}/reports/caseload-review/${headerId}/details`);
      setCaseloadReviewDetails(prev => ({
        ...prev,
        [headerId]: response.data
      }));
    } catch (error) {
      console.error('Failed to load caseload review details:', error);
      toast.error('Failed to load participant details');
    }
  };

  const toggleCaseloadReviewExpanded = (headerId) => {
    const isCurrentlyExpanded = expandedCaseloadReviews[headerId];
    setExpandedCaseloadReviews(prev => ({
      ...prev,
      [headerId]: !prev[headerId]
    }));
    
    // Fetch details if expanding and not already loaded
    if (!isCurrentlyExpanded && !caseloadReviewDetails[headerId]) {
      fetchCaseloadReviewDetails(headerId);
    }
  };

  const exportCaseloadReviewCsv = () => {
    if (!caseloadReviewData?.summary) return;
    
    const headers = [
      'Week Of', 'Program', 'Case Manager', 'Status', 'Total Caseload',
      'Pathways Enrollments', 'HELP Institute Enrollments', 'Active', 'Inactive',
      'Pending Start', 'Exited', 'Reviewed', 'Remaining', 'Completed Date'
    ];
    
    const rows = caseloadReviewData.summary.map(row => [
      row.week_of,
      row.program_name || row.program_type,
      row.case_manager_name,
      row.review_status,
      row.total_caseload_size,
      row.pathways_enrollments,
      row.help_institute_enrollments,
      row.total_active,
      row.total_inactive,
      row.total_pending_start,
      row.total_exited_this_week,
      row.participants_reviewed,
      row.participants_remaining,
      row.completed_at ? formatDateUtil(row.completed_at) : ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Caseload_Review_Report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Caseload Review report exported');
  };

  // State to track which caseload review PDFs are being exported
  const [exportingCaseloadReviewPdf, setExportingCaseloadReviewPdf] = useState({});

  const exportCaseloadReviewPdf = async (headerId, e) => {
    // Stop row expansion when clicking PDF button
    if (e) e.stopPropagation();
    
    setExportingCaseloadReviewPdf(prev => ({ ...prev, [headerId]: true }));
    try {
      const response = await axios.post(
        `${API}/reports/caseload-review/${headerId}/export-pdf`,
        {},
        { responseType: 'blob' }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Generate filename
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `Caseload_Review_${headerId}_${dateStr}.pdf`;
      
      // Open in new tab for preview
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
      }
      
      toast.success('Caseload Review PDF generated');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export Caseload Review PDF');
    } finally {
      setExportingCaseloadReviewPdf(prev => ({ ...prev, [headerId]: false }));
    }
  };

  // Community Service Report functions
  const fetchCommunityServiceReport = async () => {
    setCommunityServiceLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('year', communityServiceFilters.year);
      if (communityServiceFilters.location !== '_all') {
        params.append('location', communityServiceFilters.location);
      }
      
      const response = await axios.get(`${API}/reports/community-service?${params}`);
      setCommunityServiceData(response.data);
    } catch (error) {
      console.error('Failed to load Community Service report:', error);
      toast.error('Failed to load Community Service report');
    } finally {
      setCommunityServiceLoading(false);
    }
  };
  
  const exportCommunityServiceReport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('year', communityServiceFilters.year);
      if (communityServiceFilters.location !== '_all') {
        params.append('location', communityServiceFilters.location);
      }
      
      const response = await axios.get(`${API}/reports/community-service/export?${params}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Community_Service_Report_${communityServiceFilters.year}.csv`;
      link.click();
      
      toast.success('Community Service report exported');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export Community Service report');
    } finally {
      setExporting(false);
    }
  };
  
  const getSortedCommunityServiceRows = () => {
    if (!communityServiceData?.rows) return [];
    const rows = [...communityServiceData.rows];
    
    rows.sort((a, b) => {
      let aVal = a[communityServiceSortConfig.key];
      let bVal = b[communityServiceSortConfig.key];
      
      // Handle numeric values
      if (['required_hours', 'completed_hours', 'remaining_hours'].includes(communityServiceSortConfig.key)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return communityServiceSortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return communityServiceSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return rows;
  };
  
  const handleCommunityServiceSort = (key) => {
    setCommunityServiceSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Participant Costs Report functions
  const fetchParticipantCostsReport = async () => {
    if (!participantCostsFilters.start_date || !participantCostsFilters.end_date) {
      toast.error('Please select both start and end dates');
      return;
    }
    setParticipantCostsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('start_date', participantCostsFilters.start_date);
      params.append('end_date', participantCostsFilters.end_date);
      if (participantCostsFilters.enrollment_type_id !== '_all') {
        params.append('enrollment_type_id', participantCostsFilters.enrollment_type_id);
      }
      if (participantCostsFilters.state !== '_all') {
        params.append('state', participantCostsFilters.state);
      }
      if (participantCostsFilters.category_id !== '_all') {
        params.append('category_id', participantCostsFilters.category_id);
      }
      
      const response = await axios.get(`${API}/reports/participant-costs?${params}`);
      setParticipantCostsData(response.data);
    } catch (error) {
      console.error('Failed to load Participant Costs report:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view this report');
      } else {
        toast.error('Failed to load Participant Costs report');
      }
    } finally {
      setParticipantCostsLoading(false);
    }
  };

  const toggleCostCategoryExpanded = (categoryId) => {
    setExpandedCostCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const exportParticipantCostsReportCsv = () => {
    if (!participantCostsData) return;
    
    const headers = ['Participant Name', 'Enrollment Type', 'Case ID', 'Transaction Date', 'Category', 'Billing Item', 'Description', 'Quantity', 'Unit Cost', 'Total Amount', 'Contract', 'Reference #', 'Status', 'Created By'];
    const rows = participantCostsData.transactions.map(t => [
      t.participant_name,
      t.enrollment_type_id,
      t.case_id,
      t.transaction_date,
      t.category_name,
      t.billing_item_name,
      t.description || '',
      t.quantity,
      t.unit_cost,
      t.total_amount,
      t.contract_name || '',
      t.reference_number || '',
      t.status,
      t.created_by_name
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Participant_Costs_Report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Participant Costs report exported');
  };

  // Employment Outcomes Report functions
  const fetchEmploymentOutcomesReport = async () => {
    if (!employmentOutcomesFilters.start_date || !employmentOutcomesFilters.end_date) {
      toast.error('Please select both start and end dates');
      return;
    }
    setEmploymentOutcomesLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('start_date', employmentOutcomesFilters.start_date);
      params.append('end_date', employmentOutcomesFilters.end_date);
      if (employmentOutcomesFilters.employment_status_at_enrollment !== '_all') {
        params.append('employment_status_at_enrollment', employmentOutcomesFilters.employment_status_at_enrollment);
      }
      if (employmentOutcomesFilters.status !== '_all') {
        params.append('status', employmentOutcomesFilters.status);
      }
      if (employmentOutcomesFilters.case_manager_id !== '_all') {
        params.append('case_manager_id', employmentOutcomesFilters.case_manager_id);
      }
      if (employmentOutcomesFilters.states && employmentOutcomesFilters.states.length > 0) {
        params.append('states', employmentOutcomesFilters.states.join(','));
      }
      
      const response = await axios.get(`${API}/reports/employment-outcomes?${params}`);
      setEmploymentOutcomesData(response.data);
    } catch (error) {
      console.error('Failed to load Employment Outcomes report:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view this report');
      } else {
        toast.error('Failed to load Employment Outcomes report');
      }
    } finally {
      setEmploymentOutcomesLoading(false);
    }
  };

  const sortEmploymentOutcomesData = (key) => {
    let direction = 'asc';
    if (employmentOutcomesSortConfig.key === key && employmentOutcomesSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setEmploymentOutcomesSortConfig({ key, direction });
  };

  const getSortedEmploymentOutcomesRecords = () => {
    if (!employmentOutcomesData?.records) return [];
    
    const records = [...employmentOutcomesData.records];
    const { key, direction } = employmentOutcomesSortConfig;
    
    records.sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];
      
      // Handle numeric values
      if (key === 'hourly_wage') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      
      // Handle string values
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue || '').toLowerCase();
      }
      
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return records;
  };

  const exportEmploymentOutcomesReport = async () => {
    if (!employmentOutcomesFilters.start_date || !employmentOutcomesFilters.end_date) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('start_date', employmentOutcomesFilters.start_date);
      params.append('end_date', employmentOutcomesFilters.end_date);
      if (employmentOutcomesFilters.employment_status_at_enrollment !== '_all') {
        params.append('employment_status_at_enrollment', employmentOutcomesFilters.employment_status_at_enrollment);
      }
      if (employmentOutcomesFilters.status !== '_all') {
        params.append('status', employmentOutcomesFilters.status);
      }
      if (employmentOutcomesFilters.case_manager_id !== '_all') {
        params.append('case_manager_id', employmentOutcomesFilters.case_manager_id);
      }
      if (employmentOutcomesFilters.states && employmentOutcomesFilters.states.length > 0) {
        params.append('states', employmentOutcomesFilters.states.join(','));
      }
      
      const response = await axios.get(`${API}/reports/employment-outcomes/export?${params}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Employment_Outcomes_Report_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      toast.success('Employment Outcomes report exported');
    } catch (error) {
      console.error('Failed to export Employment Outcomes report:', error);
      toast.error('Failed to export Employment Outcomes report');
    } finally {
      setExporting(false);
    }
  };

  // Retention Report functions
  const fetchRetentionReport = async () => {
    setRetentionLoading(true);
    try {
      const params = new URLSearchParams();
      if (retentionFilters.start_date) params.append('start_date', retentionFilters.start_date);
      if (retentionFilters.end_date) params.append('end_date', retentionFilters.end_date);
      if (retentionFilters.states && retentionFilters.states.length > 0) {
        params.append('states', retentionFilters.states.join(','));
      }
      
      const response = await axios.get(`${API}/reports/retention?${params}`);
      setRetentionData(response.data);
    } catch (error) {
      console.error('Failed to load Retention report:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view this report');
      } else {
        toast.error('Failed to load Retention report');
      }
    } finally {
      setRetentionLoading(false);
    }
  };

  const sortRetentionData = (key) => {
    let direction = 'asc';
    if (retentionSortConfig.key === key && retentionSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setRetentionSortConfig({ key, direction });
  };

  const getSortedRetentionRecords = () => {
    if (!retentionData?.records) return [];
    
    const records = [...retentionData.records];
    const { key, direction } = retentionSortConfig;
    
    records.sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];
      
      // Handle string values
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue || '').toLowerCase();
      }
      
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return records;
  };

  const exportRetentionReport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (retentionFilters.start_date) params.append('start_date', retentionFilters.start_date);
      if (retentionFilters.end_date) params.append('end_date', retentionFilters.end_date);
      if (retentionFilters.states && retentionFilters.states.length > 0) {
        params.append('states', retentionFilters.states.join(','));
      }
      
      const response = await axios.get(`${API}/reports/retention/export?${params}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `SNAP_ET_Retention_Report_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      toast.success('Retention report exported');
    } catch (error) {
      console.error('Failed to export Retention report:', error);
      toast.error('Failed to export Retention report');
    } finally {
      setExporting(false);
    }
  };

  // Master Enrollment Metrics Report functions
  const fetchFiscalGoals = async () => {
    try {
      const response = await axios.get(`${API}/reports/master-enrollment-metrics/fiscal-goals`);
      setFiscalGoals(response.data.goals || {});
    } catch (error) {
      console.error('Failed to fetch fiscal goals:', error);
    }
  };

  const saveFiscalGoal = async (state, goal) => {
    try {
      await axios.put(`${API}/reports/master-enrollment-metrics/fiscal-goal`, { state, goal });
      setFiscalGoals(prev => ({ ...prev, [state]: goal }));
      toast.success('Fiscal goal saved');
    } catch (error) {
      console.error('Failed to save fiscal goal:', error);
      toast.error('Failed to save fiscal goal');
    }
  };

  const fetchMasterEnrollmentReport = async () => {
    setMasterEnrollmentLoading(true);
    try {
      const params = new URLSearchParams();
      if (masterEnrollmentFilters.start_date) params.append('start_date', masterEnrollmentFilters.start_date);
      if (masterEnrollmentFilters.end_date) params.append('end_date', masterEnrollmentFilters.end_date);
      if (masterEnrollmentFilters.states && masterEnrollmentFilters.states.length > 0) {
        params.append('states', masterEnrollmentFilters.states.join(','));
      }
      
      const response = await axios.get(`${API}/reports/master-enrollment-metrics?${params}`);
      setMasterEnrollmentData(response.data);
      
      // Update current fiscal goal based on state filter
      const stateKey = masterEnrollmentFilters.states.length === 1 ? masterEnrollmentFilters.states[0] : 'ALL';
      setCurrentFiscalGoal(response.data.summary?.fiscal_goal || fiscalGoals[stateKey] || 0);
    } catch (error) {
      console.error('Failed to load Master Enrollment report:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view this report');
      } else {
        toast.error('Failed to load Master Enrollment report');
      }
    } finally {
      setMasterEnrollmentLoading(false);
    }
  };

  const sortMasterEnrollmentData = (key) => {
    let direction = 'asc';
    if (masterEnrollmentSortConfig.key === key && masterEnrollmentSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setMasterEnrollmentSortConfig({ key, direction });
  };

  const getSortedMasterEnrollmentRecords = () => {
    if (!masterEnrollmentData?.records) return [];
    
    // Sort First Enrollment rows; each Second Enrollment stays directly under its First Enrollment
    const allRecords = masterEnrollmentData.records;
    const records = allRecords.filter(r => !r.is_second_enrollment);
    const { key, direction } = masterEnrollmentSortConfig;
    
    records.sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue || '').toLowerCase();
      }
      
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    const seconds = allRecords.filter(r => r.is_second_enrollment);
    const placed = new Set();
    const grouped = [];
    records.forEach(first => {
      grouped.push(first);
      seconds.forEach((sec, i) => {
        if (!placed.has(i) && sec.enrollment_id === first.enrollment_id) {
          grouped.push(sec);
          placed.add(i);
        }
      });
    });
    seconds.forEach((sec, i) => { if (!placed.has(i)) grouped.push(sec); });
    return grouped;
  };

  const handleFiscalGoalChange = (value) => {
    const goal = parseInt(value) || 0;
    setCurrentFiscalGoal(goal);
    
    // Save the goal for the current state filter
    const stateKey = masterEnrollmentFilters.states.length === 1 ? masterEnrollmentFilters.states[0] : 'ALL';
    saveFiscalGoal(stateKey, goal);
    
    // Update the displayed data
    if (masterEnrollmentData) {
      setMasterEnrollmentData(prev => ({
        ...prev,
        summary: {
          ...prev.summary,
          fiscal_goal: goal,
          remaining_enrollments: Math.max(0, goal - prev.summary.total_enrollments)
        }
      }));
    }
  };

  const exportMasterEnrollmentReport = async (billing = false) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (masterEnrollmentFilters.start_date) params.append('start_date', masterEnrollmentFilters.start_date);
      if (masterEnrollmentFilters.end_date) params.append('end_date', masterEnrollmentFilters.end_date);
      if (masterEnrollmentFilters.states && masterEnrollmentFilters.states.length > 0) {
        params.append('states', masterEnrollmentFilters.states.join(','));
      }
      params.append('fiscal_goal', currentFiscalGoal);
      if (billing) params.append('billing', 'true');
      
      const response = await axios.get(`${API}/reports/master-enrollment-metrics/export?${params}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `SNAP_ET_Master_Enrollment_Metrics_${billing ? 'Billing_' : ''}${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      toast.success(billing ? 'Billing CSV exported' : 'Master Enrollment report exported');
    } catch (error) {
      console.error('Failed to export Master Enrollment report:', error);
      toast.error('Failed to export Master Enrollment report');
    } finally {
      setExporting(false);
    }
  };

  // Handle billing status toggle
  const handleBillingStatusToggle = async (enrollmentId, currentStatus) => {
    const newStatus = currentStatus === 'Billed' ? 'Unbilled' : 'Billed';
    
    try {
      await axios.put(`${API}/reports/master-enrollment-metrics/billing-status/${enrollmentId}`, {
        billing_status: newStatus
      });
      
      // Update the local state to reflect the change
      setMasterEnrollmentData(prev => {
        if (!prev) return prev;
        
        // Update the record in the list
        const updatedRecords = prev.records.map(record => 
          record.enrollment_id === enrollmentId 
            ? { ...record, billing_status: newStatus }
            : record
        );
        
        // Recalculate summary counts
        const firstRecords = updatedRecords.filter(r => !r.is_second_enrollment);
        const billedCount = firstRecords.filter(r => r.billing_status === 'Billed').length;
        const unbilledCount = firstRecords.filter(r => r.billing_status === 'Unbilled').length;
        
        return {
          ...prev,
          records: updatedRecords,
          summary: {
            ...prev.summary,
            total_billed: billedCount,
            total_unbilled: unbilledCount
          }
        };
      });
      
      toast.success(`Billing status updated to ${newStatus}`);
    } catch (error) {
      console.error('Failed to update billing status:', error);
      toast.error('Failed to update billing status');
    }
  };

  // Load fiscal goals when component mounts
  useEffect(() => {
    if (reportType === 'master-enrollment-metrics') {
      fetchFiscalGoals();
    }
  }, [reportType]);

  // Update current fiscal goal when state filter changes
  useEffect(() => {
    const stateKey = masterEnrollmentFilters.states.length === 1 ? masterEnrollmentFilters.states[0] : 'ALL';
    setCurrentFiscalGoal(fiscalGoals[stateKey] || 0);
  }, [masterEnrollmentFilters.states, fiscalGoals]);

  const handleExport = async (reportType) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await axios.get(`${API}/reports/export/${reportType}?${params}`);
      const { headers, data } = response.data;
      
      // Convert to CSV
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      // Generate clean filename with report type and date
      const reportNames = {
        'enrollments': 'Enrollment_Report',
        'caseload': 'Caseload_Report',
        'participants': 'Participants_Report',
        'contracts': 'Contracts_Report',
        'audit': 'Audit_Log_Report'
      };
      const reportDisplayName = reportNames[reportType] || reportType.replace(/-/g, '_') + '_Report';
      const dateStr = new Date().toISOString().split('T')[0];
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${reportDisplayName}_${dateStr}.csv`;
      link.click();
      
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async (reportType, additionalParams = {}) => {
    setExportingPdf(true);
    try {
      const response = await axios.post(
        `${API}/reports/${reportType}/export-pdf`,
        {
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          days_ahead: additionalParams.days_ahead || snapDaysAhead,
          filters: additionalParams.filters || {}
        },
        { responseType: 'blob' }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Generate clean filename with report type and date
      const reportNames = {
        'enrollments': 'Enrollment_Report',
        'caseload': 'Caseload_Report',
        'participants': 'Participants_Report',
        'contracts': 'Contracts_Report',
        'audit': 'Audit_Log_Report',
        'snap-cert-expirations': 'SNAP_Certification_Expirations_Report',
        'snap-enrollment-expirations': 'SNAP_Enrollment_Expirations_Report',
        'food-pantry-checkins': 'Food_Pantry_Checkins_Report',
        'snap-et-caseload-management': 'SNAP_ET_Caseload_Management_Report'
      };
      const reportDisplayName = reportNames[reportType] || reportType.replace(/-/g, '_') + '_Report';
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `${reportDisplayName}_${dateStr}.pdf`;
      
      // Open in new tab for preview
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        // If popup blocked, fallback to direct download
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
      }
      
      toast.success('PDF report generated successfully');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF report');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    const now = new Date();
    let start = '';
    let end = now.toISOString().split('T')[0];
    
    switch (value) {
      case 'today':
        start = end;
        break;
      case 'week':
        start = new Date(now.setDate(now.getDate() - 7)).toISOString().split('T')[0];
        break;
      case 'month':
        start = new Date(now.setMonth(now.getMonth() - 1)).toISOString().split('T')[0];
        break;
      case 'quarter':
        start = new Date(now.setMonth(now.getMonth() - 3)).toISOString().split('T')[0];
        break;
      case 'year':
        start = new Date(now.setFullYear(now.getFullYear() - 1)).toISOString().split('T')[0];
        break;
      case 'custom':
        return; // User will set manually
      default:
        start = '';
        end = '';
    }
    setStartDate(start);
    setEndDate(value === 'all' ? '' : end);
  };

  const applyEnrollmentFilters = () => {
    fetchEnrollmentsReport();
    setShowFilters(false);
  };

  const clearEnrollmentFilters = () => {
    setEnrollmentFilters({
      program_type: '_all',
      state: '_all',
      contract: '_all',
      status: 'all',
      case_manager_id: '_all',
      county: '_all'
    });
    // Re-fetch with cleared filters
    setTimeout(() => fetchEnrollmentsReport(), 100);
  };

  const generateCustomReport = async () => {
    setCustomReportLoading(true);
    try {
      // Build filters properly
      const filters = {};
      if (customReportConfig.filters.start_date) {
        filters.start_date = customReportConfig.filters.start_date;
      }
      if (customReportConfig.filters.end_date) {
        filters.end_date = customReportConfig.filters.end_date;
      }
      if (customReportConfig.filters.enrollment_type && customReportConfig.filters.enrollment_type !== '_all') {
        filters.enrollment_type = customReportConfig.filters.enrollment_type;
      }
      if (customReportConfig.filters.state && customReportConfig.filters.state !== '_all') {
        filters.state = customReportConfig.filters.state;
      }
      if (customReportConfig.filters.status && customReportConfig.filters.status !== 'all') {
        filters.status = customReportConfig.filters.status;
      }
      
      const response = await axios.post(`${API}/reports/custom`, {
        entity_type: customReportConfig.entity_type,
        sort_by: customReportConfig.sort_by,
        sort_order: customReportConfig.sort_order,
        limit: customReportConfig.limit,
        filters
      });
      setCustomReportData(response.data);
      toast.success(`Custom report generated with ${response.data.total_records} records`);
    } catch (error) {
      toast.error('Failed to generate custom report');
    } finally {
      setCustomReportLoading(false);
    }
  };

  const exportCustomReport = () => {
    if (!customReportData) return;
    
    const { headers, data } = customReportData;
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    
    // Generate clean filename with report type and date
    const entityNames = {
      'enrollments': 'Custom_Enrollments',
      'cases': 'Custom_Cases',
      'participants': 'Custom_Participants',
      'case_notes': 'Custom_Case_Notes'
    };
    const reportName = entityNames[customReportConfig.entity_type] || 'Custom_Report';
    const dateStr = new Date().toISOString().split('T')[0];
    
    link.download = `${reportName}_Report_${dateStr}.csv`;
    link.click();
    toast.success('Custom report exported');
  };

  const exportCustomReportPdf = async () => {
    if (!customReportData) return;
    
    setExportingPdf(true);
    try {
      const response = await axios.post(
        `${API}/reports/custom-report/export-pdf`,
        {
          entity_type: customReportConfig.entity_type,
          headers: customReportData.headers,
          data: customReportData.data,
          total_records: customReportData.total_records,
          filters: customReportConfig.filters,
          start_date: customReportConfig.filters.start_date || undefined,
          end_date: customReportConfig.filters.end_date || undefined
        },
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Generate clean filename with report type and date
      const entityNames = {
        'enrollments': 'Custom_Enrollments',
        'cases': 'Custom_Cases',
        'participants': 'Custom_Participants',
        'case_notes': 'Custom_Case_Notes'
      };
      const reportName = entityNames[customReportConfig.entity_type] || 'Custom_Report';
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `${reportName}_Report_${dateStr}.pdf`;
      
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
      }
      
      toast.success('Custom PDF report generated successfully');
    } catch (error) {
      console.error('Custom PDF export error:', error);
      toast.error('Failed to export custom PDF report');
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const canViewContracts = summary?.can_view_contracts;
  const canViewAudit = summary?.can_view_audit;
  const activeFiltersCount = Object.entries(enrollmentFilters).filter(([k, v]) => v && v !== 'all' && v !== '_all').length;

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Back button for dedicated report pages */}
      {isDedicatedReportPage && (
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" asChild className="gap-2">
            <Link to="/reports">
              <ArrowLeft className="w-4 h-4" />
              Back to Reports
            </Link>
          </Button>
        </div>
      )}

      {/* Dashboard sections - hidden when viewing dedicated report page */}
      {!isDedicatedReportPage && (
        <>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                <BarChart3 className="w-8 h-8 text-primary" />
                Reports
              </h1>
              <p className="text-muted-foreground mt-1">
                Analytics and insights across your organization
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={dateRange} onValueChange={handleDateRangeChange}>
                <SelectTrigger className="w-40" data-testid="date-range-select">
                  <SelectValue placeholder="Date range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                  <SelectItem value="quarter">Last Quarter</SelectItem>
                  <SelectItem value="year">Last Year</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
              
              <Dialog open={showCustomReport} onOpenChange={setShowCustomReport}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="custom-report-btn">
                    <Settings2 className="w-4 h-4 mr-2" />
                    Custom Report
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Custom Report Builder</DialogTitle>
                    <DialogDescription>
                      Create a custom report by selecting the data you need
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Entity Type</Label>
                        <Select 
                          value={customReportConfig.entity_type} 
                          onValueChange={(v) => setCustomReportConfig(prev => ({...prev, entity_type: v}))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="enrollments">Enrollments</SelectItem>
                        <SelectItem value="participants">Participants</SelectItem>
                        <SelectItem value="cases">Cases</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Enrollment Type</Label>
                    <Select 
                      value={customReportConfig.filters.enrollment_type || '_all'} 
                      onValueChange={(v) => setCustomReportConfig(prev => ({
                        ...prev, 
                        filters: {...prev.filters, enrollment_type: v}
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all">All Enrollment Types</SelectItem>
                        {enrollmentTypes.map(et => (
                          <SelectItem key={et.id} value={et.id}>{et.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Date Range Section */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Date Range
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Start Date</Label>
                      <Input
                        type="date"
                        value={customReportConfig.filters.start_date || ''}
                        onChange={(e) => setCustomReportConfig(prev => ({
                          ...prev,
                          filters: {...prev.filters, start_date: e.target.value}
                        }))}
                        data-testid="custom-report-start-date"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">End Date</Label>
                      <Input
                        type="date"
                        value={customReportConfig.filters.end_date || ''}
                        onChange={(e) => setCustomReportConfig(prev => ({
                          ...prev,
                          filters: {...prev.filters, end_date: e.target.value}
                        }))}
                        data-testid="custom-report-end-date"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Max Records</Label>
                    <Select 
                      value={String(customReportConfig.limit)} 
                      onValueChange={(v) => setCustomReportConfig(prev => ({...prev, limit: parseInt(v)}))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="500">500</SelectItem>
                        <SelectItem value="1000">1,000</SelectItem>
                        <SelectItem value="5000">5,000</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Sort By</Label>
                    <Select 
                      value={customReportConfig.sort_by} 
                      onValueChange={(v) => setCustomReportConfig(prev => ({...prev, sort_by: v}))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="start_date">Start Date</SelectItem>
                        <SelectItem value="created_at">Created Date</SelectItem>
                        <SelectItem value="participant_name">Participant Name</SelectItem>
                        <SelectItem value="status">Status</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Sort Order</Label>
                    <Select 
                      value={customReportConfig.sort_order} 
                      onValueChange={(v) => setCustomReportConfig(prev => ({...prev, sort_order: v}))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="desc">Newest First</SelectItem>
                        <SelectItem value="asc">Oldest First</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Select 
                      value={customReportConfig.filters.state || '_all'} 
                      onValueChange={(v) => setCustomReportConfig(prev => ({
                        ...prev, 
                        filters: {...prev.filters, state: v}
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All States" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all">All States</SelectItem>
                        {filterOptions?.states?.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button onClick={generateCustomReport} disabled={customReportLoading}>
                    {customReportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                    Generate Report
                  </Button>
                  {customReportData && (
                    <>
                      <Button variant="outline" onClick={exportCustomReport}>
                        <Download className="w-4 h-4 mr-2" />
                        Export CSV
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={exportCustomReportPdf}
                        disabled={exportingPdf}
                        data-testid="export-custom-pdf-btn"
                      >
                        {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                        Export PDF
                      </Button>
                    </>
                  )}
                  <Button 
                    variant="ghost" 
                    onClick={() => setCustomReportConfig(prev => ({
                      ...prev,
                      filters: { enrollment_type: '_all', start_date: '', end_date: '', state: '_all' }
                    }))}
                  >
                    Clear Filters
                  </Button>
                </div>

                {customReportData && (
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground mb-2">
                      {customReportData.total_records} records found
                    </p>
                    <div className="max-h-64 overflow-auto border rounded-md">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {customReportData.headers.slice(0, 6).map((header, i) => (
                              <TableHead key={i} className="text-xs">{header}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {customReportData.data.slice(0, 10).map((row, i) => (
                            <TableRow key={i}>
                              {customReportData.headers.slice(0, 6).map((header, j) => (
                                <TableCell key={j} className="text-xs py-2">
                                  {String(row[header] || '').substring(0, 30)}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Food Pantry Reports Quick Access */}
      <Card className="border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            Food Pantry Reports
          </CardTitle>
          <CardDescription>Monthly distribution reports by location</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" asChild data-testid="charlotte-report-link">
              <Link to="/reports/charlotte-food-pantry" className="flex items-center gap-2">
                Charlotte Food Pantry
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button variant="outline" asChild data-testid="nashville-report-link">
              <Link to="/reports/nashville-food-pantry" className="flex items-center gap-2">
                Nashville Food Pantry
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Addictions Recovery Program Report - Leadership Only */}
      {canViewSnapOutreach && (
        <Card className="card-shadow border-teal-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-teal-600" />
              Addictions Recovery Program Report
            </CardTitle>
            <CardDescription>Tennessee Addictions Recovery Program services report</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" className="border-teal-200 hover:bg-teal-50" asChild data-testid="arp-report-link">
                <Link to="/reports/addictions-recovery-program" className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-teal-600" />
                  Addictions Recovery Program - Tennessee
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* SNAP E&T Program Reports */}
      <Card className="card-shadow border-purple-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-purple-600" />
            SNAP E&T Program Reports
          </CardTitle>
          <CardDescription>SNAP Employment & Training program reports and analytics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button 
              variant="outline" 
              className="border-purple-200 hover:bg-purple-50" 
              asChild
              data-testid="snap-cert-exp-report-link"
            >
              <Link to="/reports/snap-cert-expirations" className="flex items-center gap-2">
                <Target className="w-4 h-4 text-purple-600" />
                SNAP Certification Expirations
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button 
              variant="outline" 
              className="border-purple-200 hover:bg-purple-50" 
              asChild
              data-testid="snap-enroll-exp-report-link"
            >
              <Link to="/reports/snap-enrollment-expirations" className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-purple-600" />
                SNAP Enrollment Expirations
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            {canViewSnapEtCaseload && (
              <Button 
                variant="outline" 
                className="border-purple-200 hover:bg-purple-50" 
                asChild
                data-testid="snap-caseload-report-link"
              >
                <Link to="/reports/snap-et-caseload" className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-purple-600" />
                  SNAP E&T Caseload
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            )}
            {canViewSnapEtCaseload && (
              <Button 
                variant="outline" 
                className="border-purple-200 hover:bg-purple-50" 
                asChild
                data-testid="caseload-review-report-link"
              >
                <Link to="/reports/caseload-review" className="flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-purple-600" />
                  Caseload Review Report
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            )}
            {FLAG_MANAGEMENT_ROLES.includes(user?.role) && (
              <Button variant="outline" className="border-purple-200 hover:bg-purple-50" asChild data-testid="caseload-review-tickets-link">
                <Link to="/reports/caseload-review-tickets" className="flex items-center gap-2">
                  <Flag className="w-4 h-4 text-purple-600" />
                  Caseload Review Tickets
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            )}
            {canViewSnapEtCaseload && (
              <Button 
                variant="outline" 
                className="border-purple-200 hover:bg-purple-50" 
                asChild
                data-testid="snap-encounters-report-link"
              >
                <Link to="/reports/snap-et-encounters" className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-600" />
                  SNAP E&T Encounters
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            )}
            {canViewSnapOutreach && (
              <>
                <Button 
                  variant="outline" 
                  className="border-purple-200 hover:bg-purple-50" 
                  asChild
                  data-testid="pathways-report-link"
                >
                  <Link to="/reports/pathways-modules" className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-purple-600" />
                    Pathways Modules
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button 
                  variant="outline" 
                  className="border-purple-200 hover:bg-purple-50" 
                  asChild
                  data-testid="help-institute-report-link"
                >
                  <Link to="/reports/help-institute-courses" className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-purple-600" />
                    HELP Institute Courses
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button 
                  variant="outline" 
                  className="border-purple-200 hover:bg-purple-50" 
                  asChild
                  data-testid="costs-report-link"
                >
                  <Link to="/reports/participant-costs" className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-purple-600" />
                    Participant Costs
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button 
                  variant="outline" 
                  className="border-purple-200 hover:bg-purple-50" 
                  asChild
                  data-testid="employment-report-link"
                >
                  <Link to="/reports/employment-outcomes" className="flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-purple-600" />
                    Employment Outcomes
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button 
                  variant="outline" 
                  className="border-purple-200 hover:bg-purple-50" 
                  asChild
                  data-testid="retention-report-link"
                >
                  <Link to="/reports/retention" className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-purple-600" />
                    Retention Report
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button 
                  variant="outline" 
                  className="border-purple-200 hover:bg-purple-50" 
                  asChild
                  data-testid="enrollment-summary-report-link"
                >
                  <Link to="/reports/enrollment-summary" className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-purple-600" />
                    Enrollment Summary
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button 
                  variant="outline" 
                  className="border-purple-200 hover:bg-purple-50" 
                  asChild
                  data-testid="master-enrollment-metrics-link"
                >
                  <Link to="/reports/master-enrollment-metrics" className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-purple-600" />
                    Master Enrollment Metrics
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* SNAP Outreach Reports */}
      {canViewSnapOutreach && (
        <Card className="card-shadow border-orange-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-orange-600" />
              SNAP Outreach Reports
            </CardTitle>
            <CardDescription>SNAP Outreach events and participant engagement analytics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button 
                variant="outline" 
                className="border-orange-200 hover:bg-orange-50" 
                asChild
                data-testid="snap-outreach-report-link"
              >
                <Link to="/reports/snap-outreach" className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-orange-600" />
                  SNAP Outreach Events
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Community Programs Reports */}
      <Card className="card-shadow border-teal-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-teal-600" />
            Community Programs Reports
          </CardTitle>
          <CardDescription>Community service and volunteer program reports</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button 
              variant="outline" 
              className="border-teal-200 hover:bg-teal-50" 
              asChild
              data-testid="community-service-report-link"
            >
              <Link to="/reports/community-service" className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-teal-600" />
                Community Service Report
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* HELPConnect Reports */}
      {user?.role === 'system_admin' && (
        <Card className="card-shadow border-purple-200" data-testid="helpconnect-reports-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LifeBuoy className="w-5 h-5 text-purple-600" />
              HELPConnect Reports
            </CardTitle>
            <CardDescription>Participant learning portal reports</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" className="border-purple-200 hover:bg-purple-50" asChild data-testid="helpdesk-tickets-report-link">
                <Link to="/reports/helpdesk-tickets" className="flex items-center gap-2">
                  <LifeBuoy className="w-4 h-4 text-purple-600" />
                  Help Desk Tickets
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Custom Date Range */}
      {dateRange === 'custom' && (
        <Card className="card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-end gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="end-date-input"
                />
              </div>
              <Button variant="outline" onClick={() => {
                if (activeTab === 'caseload') fetchCaseloadReport();
                else if (activeTab === 'participants') fetchParticipantsReport();
                else if (activeTab === 'audit') fetchAuditReport();
                else if (activeTab === 'enrollments') fetchEnrollmentsReport();
              }}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Apply
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <ClipboardList className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Enrollments</p>
                <p className="text-2xl font-bold">{summary?.enrollments?.total || 0}</p>
                <p className="text-xs text-green-600">{summary?.enrollments?.active || 0} active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Cases</p>
                <p className="text-2xl font-bold">{summary?.cases?.total || 0}</p>
                <p className="text-xs text-blue-600">{summary?.cases?.open || 0} open</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Participants</p>
                <p className="text-2xl font-bold">{summary?.participants?.total || 0}</p>
                <p className="text-xs text-purple-600">{summary?.participants?.active_enrollments || 0} enrolled</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Building className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Contracts</p>
                <p className="text-2xl font-bold">{summary?.contracts?.total || 0}</p>
                <p className="text-xs text-green-600">{summary?.contracts?.active || 0} active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <Shield className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Audit Logs</p>
                <p className="text-2xl font-bold">
                  {canViewAudit ? (summary?.audit_logs || 0) : <Lock className="w-5 h-5 inline" />}
                </p>
                <p className="text-xs text-muted-foreground">{canViewAudit ? 'Total events' : 'Admin only'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      </>
      )}

      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Tab navigation - hidden for dedicated report pages */}
        {!isDedicatedReportPage && (
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="enrollments" data-testid="tab-enrollments">
            <ClipboardList className="w-4 h-4 mr-2" />
            Enrollments
          </TabsTrigger>
          <TabsTrigger value="caseload" data-testid="tab-caseload">
            <FileText className="w-4 h-4 mr-2" />
            Caseload
          </TabsTrigger>
          <TabsTrigger value="participants" data-testid="tab-participants">
            <Users className="w-4 h-4 mr-2" />
            Participants
          </TabsTrigger>
          <TabsTrigger value="contracts" data-testid="tab-contracts" disabled={!canViewContracts}>
            <Building className="w-4 h-4 mr-2" />
            Contracts
            {!canViewContracts && <Lock className="w-3 h-3 ml-1" />}
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit" disabled={!canViewAudit}>
            <Shield className="w-4 h-4 mr-2" />
            Audit
            {!canViewAudit && <Lock className="w-3 h-3 ml-1" />}
          </TabsTrigger>
        </TabsList>
        )}

        {/* Enrollments Report - NEW */}
        <TabsContent value="enrollments" className="space-y-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Dialog open={showFilters} onOpenChange={setShowFilters}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="filter-enrollments-btn">
                    <Filter className="w-4 h-4 mr-2" />
                    Filters
                    {activeFiltersCount > 0 && (
                      <Badge variant="secondary" className="ml-2">{activeFiltersCount}</Badge>
                    )}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Filter Enrollments</DialogTitle>
                    <DialogDescription>
                      Narrow down the report by program, state, contract, or case manager
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Program</Label>
                      <Select 
                        value={enrollmentFilters.program_type} 
                        onValueChange={(v) => setEnrollmentFilters(prev => ({...prev, program_type: v}))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All programs" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">All Programs</SelectItem>
                          {filterOptions?.programs?.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Select 
                        value={enrollmentFilters.state} 
                        onValueChange={(v) => setEnrollmentFilters(prev => ({...prev, state: v}))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All states" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">All States</SelectItem>
                          {filterOptions?.states?.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Contract</Label>
                      <Select 
                        value={enrollmentFilters.contract} 
                        onValueChange={(v) => setEnrollmentFilters(prev => ({...prev, contract: v}))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All contracts" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">All Contracts</SelectItem>
                          {filterOptions?.contracts?.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select 
                        value={enrollmentFilters.status} 
                        onValueChange={(v) => setEnrollmentFilters(prev => ({...prev, status: v}))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Case Manager</Label>
                      <Select 
                        value={enrollmentFilters.case_manager_id} 
                        onValueChange={(v) => setEnrollmentFilters(prev => ({...prev, case_manager_id: v}))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All case managers" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">All Case Managers</SelectItem>
                          {filterOptions?.case_managers?.map(cm => (
                            <SelectItem key={cm.id} value={cm.id}>{cm.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>County</Label>
                      <Select 
                        value={enrollmentFilters.county} 
                        onValueChange={(v) => setEnrollmentFilters(prev => ({...prev, county: v}))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All counties" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">All Counties</SelectItem>
                          {filterOptions?.counties?.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex gap-2 pt-4">
                      <Button onClick={applyEnrollmentFilters}>Apply Filters</Button>
                      <Button variant="outline" onClick={clearEnrollmentFilters}>Clear All</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              
              {activeFiltersCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearEnrollmentFilters}>
                  <X className="w-4 h-4 mr-1" />
                  Clear filters
                </Button>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Button onClick={() => handleExport('enrollments')} disabled={exporting} data-testid="export-enrollments-btn">
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Export CSV
              </Button>
              <Button 
                variant="outline" 
                onClick={() => handleExportPdf('enrollments')} 
                disabled={exportingPdf}
                data-testid="export-enrollments-pdf-btn"
              >
                {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                Export PDF
              </Button>
            </div>
          </div>
          
          {enrollmentsData && (
            <div className="space-y-6">
              {/* Summary stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="card-shadow">
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Total Enrollments</p>
                    <p className="text-2xl font-bold">{enrollmentsData.total_enrollments}</p>
                  </CardContent>
                </Card>
                <Card className="card-shadow">
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Active Enrollments</p>
                    <p className="text-2xl font-bold text-green-600">{enrollmentsData.active_enrollments}</p>
                  </CardContent>
                </Card>
                <Card className="card-shadow">
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Closed Enrollments</p>
                    <p className="text-2xl font-bold text-gray-600">{enrollmentsData.closed_enrollments}</p>
                  </CardContent>
                </Card>
                <Card className="card-shadow">
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">With Active Cases</p>
                    <p className="text-2xl font-bold text-blue-600">{enrollmentsData.with_active_cases}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Enrollments by Program */}
                <Card className="card-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg">Enrollments by Program</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={enrollmentsData.enrollments_by_program} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="program" type="category" width={150} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#92298d" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Enrollments by Case Manager */}
                <Card className="card-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg">Top Case Managers by Enrollments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={enrollmentsData.enrollments_by_case_manager} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="case_manager" type="category" width={120} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#22c55e" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Enrollments by State */}
                <Card className="card-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg">Enrollments by State</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={enrollmentsData.enrollments_by_state}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          dataKey="count"
                          nameKey="state"
                          label={({ state, count }) => `${state}: ${count}`}
                        >
                          {enrollmentsData.enrollments_by_state.map((_, index) => (
                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Monthly Trend */}
                <Card className="card-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg">Enrollments Monthly Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={enrollmentsData.monthly_trend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="count" stroke="#92298d" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed enrollments table */}
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">Enrollment Details</CardTitle>
                  <CardDescription>
                    {enrollmentsData.total_enrollments} enrollments found
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="max-h-[600px] overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Participant</TableHead>
                          <TableHead>Program</TableHead>
                          <TableHead>State</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Start Date</TableHead>
                          <TableHead>End Date</TableHead>
                          <TableHead>Close Date</TableHead>
                          <TableHead>Case Manager</TableHead>
                          <TableHead>Cases</TableHead>
                          <TableHead>Outcomes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {enrollmentsData.enrollments.map((enrollment) => (
                          <TableRow key={enrollment.enrollment_id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{enrollment.participant_name}</p>
                                {enrollment.ssn_masked && (
                                  <p className="text-xs text-muted-foreground">{enrollment.ssn_masked}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium text-sm">{enrollment.program_name}</p>
                                {enrollment.contract_name && (
                                  <p className="text-xs text-muted-foreground">{enrollment.contract_name}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{enrollment.state}</TableCell>
                            <TableCell>
                              <Badge variant={enrollment.enrollment_status === 'open' ? 'default' : 'secondary'}>
                                {enrollment.enrollment_status}
                              </Badge>
                            </TableCell>
                            <TableCell>{enrollment.start_date}</TableCell>
                            <TableCell>{enrollment.end_date}</TableCell>
                            <TableCell>{enrollment.close_date || '-'}</TableCell>
                            <TableCell>{enrollment.case_manager_name}</TableCell>
                            <TableCell>
                              <Badge variant={enrollment.has_active_case ? 'default' : 'outline'}>
                                {enrollment.case_count} {enrollment.has_active_case && '(active)'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {enrollment.targeted_outcomes ? (
                                <div className="text-xs">
                                  {enrollment.targeted_outcomes.program_name && (
                                    <p>{enrollment.targeted_outcomes.program_name}</p>
                                  )}
                                  {enrollment.targeted_outcomes.employment_goal && (
                                    <p className="text-muted-foreground truncate max-w-[150px]">
                                      Goal: {enrollment.targeted_outcomes.employment_goal}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* SNAP E&T Certification Expirations Report */}
        <TabsContent value="snap-cert-expirations" className="space-y-6" data-testid="snap-cert-expirations-tab">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Label className="whitespace-nowrap">Show expirations within:</Label>
              <Select value={snapDaysAhead.toString()} onValueChange={(v) => setSnapDaysAhead(parseInt(v))}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchSnapCertExpirationsReport}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
            <Button 
              variant="outline" 
              onClick={() => handleExportPdf('snap-cert-expirations', { days_ahead: snapDaysAhead })} 
              disabled={exportingPdf}
              data-testid="export-snap-cert-pdf-btn"
            >
              {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Export PDF
            </Button>
          </div>

          {snapCertExpirationsData ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Cert. Expiring</p>
                        <p className="text-2xl font-bold text-purple-700">{snapCertExpirationsData.summary.total_expiring}</p>
                      </div>
                      <Target className="w-8 h-8 text-purple-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Critical (≤7 days)</p>
                        <p className="text-2xl font-bold text-red-700">{snapCertExpirationsData.summary.critical_within_7_days}</p>
                      </div>
                      <Calendar className="w-8 h-8 text-red-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-yellow-50 to-white border-yellow-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Warning (≤14 days)</p>
                        <p className="text-2xl font-bold text-yellow-700">{snapCertExpirationsData.summary.warning_within_14_days}</p>
                      </div>
                      <Calendar className="w-8 h-8 text-yellow-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Expiring Certifications Table */}
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">SNAP E&T Certification Periods Expiring</CardTitle>
                  <CardDescription>Cases with certification end dates within {snapDaysAhead} days (from Targeted Outcomes card)</CardDescription>
                </CardHeader>
                <CardContent>
                  {snapCertExpirationsData.expiring_cases.length > 0 ? (
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Urgency</TableHead>
                            <TableHead>Participant</TableHead>
                            <TableHead>Case ID</TableHead>
                            <TableHead>Program</TableHead>
                            <TableHead>Cert. End Date</TableHead>
                            <TableHead>Days Left</TableHead>
                            <TableHead>Assigned To</TableHead>
                            <TableHead>Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {snapCertExpirationsData.expiring_cases.map((item) => (
                            <TableRow key={item.case_id}>
                              <TableCell>
                                <Badge 
                                  variant={item.urgency === 'critical' ? 'destructive' : item.urgency === 'warning' ? 'warning' : 'secondary'}
                                  className={item.urgency === 'critical' ? 'bg-red-500' : item.urgency === 'warning' ? 'bg-yellow-500' : ''}
                                >
                                  {item.urgency === 'critical' ? '⚠️ Critical' : item.urgency === 'warning' ? '⏰ Warning' : 'Normal'}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-medium">{item.participant_name}</TableCell>
                              <TableCell className="font-mono text-sm">{item.case_id}</TableCell>
                              <TableCell>{item.program_name}</TableCell>
                              <TableCell>{item.certification_end_date}</TableCell>
                              <TableCell>
                                <Badge variant={item.days_until_expiration <= 7 ? 'destructive' : 'outline'}>
                                  {item.days_until_expiration} days
                                </Badge>
                              </TableCell>
                              <TableCell>{item.assigned_staff_name}</TableCell>
                              <TableCell>
                                <Link to={`/cases/${item.case_id}`}>
                                  <Button variant="outline" size="sm">
                                    <Eye className="w-4 h-4 mr-1" />
                                    View
                                  </Button>
                                </Link>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No SNAP E&T certification periods expiring within {snapDaysAhead} days</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}
        </TabsContent>

        {/* SNAP E&T Enrollment Expirations Report */}
        <TabsContent value="snap-enrollment-expirations" className="space-y-6" data-testid="snap-enrollment-expirations-tab">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Label className="whitespace-nowrap">Show expirations within:</Label>
              <Select value={snapDaysAhead.toString()} onValueChange={(v) => setSnapDaysAhead(parseInt(v))}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchSnapEnrollmentExpirationsReport}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
            <Button 
              variant="outline" 
              onClick={() => handleExportPdf('snap-enrollment-expirations', { days_ahead: snapDaysAhead })} 
              disabled={exportingPdf}
              data-testid="export-snap-enrollment-pdf-btn"
            >
              {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Export PDF
            </Button>
          </div>

          {snapEnrollmentExpirationsData ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Expiring</p>
                        <p className="text-2xl font-bold text-blue-700">{snapEnrollmentExpirationsData.summary.total_expiring}</p>
                      </div>
                      <Calendar className="w-8 h-8 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Critical (≤7 days)</p>
                        <p className="text-2xl font-bold text-red-700">{snapEnrollmentExpirationsData.summary.critical_within_7_days}</p>
                      </div>
                      <Calendar className="w-8 h-8 text-red-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-yellow-50 to-white border-yellow-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Warning (≤14 days)</p>
                        <p className="text-2xl font-bold text-yellow-700">{snapEnrollmentExpirationsData.summary.warning_within_14_days}</p>
                      </div>
                      <Calendar className="w-8 h-8 text-yellow-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Expiring Enrollments Table */}
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">SNAP E&T Enrollments Expiring</CardTitle>
                  <CardDescription>Enrollments with end dates within {snapDaysAhead} days</CardDescription>
                </CardHeader>
                <CardContent>
                  {snapEnrollmentExpirationsData.expiring_items.length > 0 ? (
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Urgency</TableHead>
                            <TableHead>Participant</TableHead>
                            <TableHead>Program</TableHead>
                            <TableHead>State</TableHead>
                            <TableHead>Start Date</TableHead>
                            <TableHead>End Date</TableHead>
                            <TableHead>Days Left</TableHead>
                            <TableHead>Case Manager</TableHead>
                            <TableHead>Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {snapEnrollmentExpirationsData.expiring_items.map((item) => (
                            <TableRow key={`enrollment-${item.id}`}>
                              <TableCell>
                                <Badge 
                                  variant={item.urgency === 'critical' ? 'destructive' : item.urgency === 'warning' ? 'warning' : 'secondary'}
                                  className={item.urgency === 'critical' ? 'bg-red-500' : item.urgency === 'warning' ? 'bg-yellow-500' : ''}
                                >
                                  {item.urgency === 'critical' ? '⚠️ Critical' : item.urgency === 'warning' ? '⏰ Warning' : 'Normal'}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-medium">{item.participant_name}</TableCell>
                              <TableCell>{item.program_name}</TableCell>
                              <TableCell>{item.state}</TableCell>
                              <TableCell>{item.start_date}</TableCell>
                              <TableCell>{item.end_date}</TableCell>
                              <TableCell>
                                <Badge variant={item.days_until_expiration <= 7 ? 'destructive' : 'outline'}>
                                  {item.days_until_expiration} days
                                </Badge>
                              </TableCell>
                              <TableCell>{item.case_manager_name}</TableCell>
                              <TableCell>
                                <Link to={`/participants/${item.participant_id}`}>
                                  <Button variant="outline" size="sm">
                                    <Eye className="w-4 h-4 mr-1" />
                                    View
                                  </Button>
                                </Link>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No SNAP E&T enrollments expiring within {snapDaysAhead} days</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}
        </TabsContent>

        {/* SNAP E&T Caseload Management Report */}
        <TabsContent value="snap-et-caseload" className="space-y-6" data-testid="snap-et-caseload-tab">
          {!canViewSnapEtCaseload ? (
            <Card className="card-shadow">
              <CardContent className="py-12 text-center">
                <Lock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold">Access Restricted</h3>
                <p className="text-muted-foreground">This report is available to System Administrators, Executives, and Operations Managers only.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Filters */}
              <Card className="card-shadow">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    Report Filters
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={snapEtFilters.start_date}
                        onChange={(e) => setSnapEtFilters(prev => ({...prev, start_date: e.target.value}))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={snapEtFilters.end_date}
                        onChange={(e) => setSnapEtFilters(prev => ({...prev, end_date: e.target.value}))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Select 
                        value={snapEtFilters.state} 
                        onValueChange={(v) => setSnapEtFilters(prev => ({...prev, state: v}))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All States" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">All States</SelectItem>
                          <SelectItem value="TN">Tennessee</SelectItem>
                          <SelectItem value="NC">North Carolina</SelectItem>
                          <SelectItem value="SC">South Carolina</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Case Status</Label>
                      <Select 
                        value={snapEtFilters.case_status} 
                        onValueChange={(v) => setSnapEtFilters(prev => ({...prev, case_status: v}))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All Statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">All Statuses</SelectItem>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={() => setSnapEtFilters({
                      start_date: '', end_date: '', office_location: '_all', program_type: '_all',
                      contract_id: '_all', state: '_all', case_manager_id: '_all', case_status: '_all'
                    })}>
                      Clear Filters
                    </Button>
                    <Button onClick={fetchSnapEtCaseloadReport} disabled={snapEtCaseloadLoading}>
                      {snapEtCaseloadLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Apply Filters
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Export Buttons */}
              <div className="flex justify-end gap-2">
                <Button onClick={exportSnapEtCaseloadCsv} disabled={!snapEtCaseloadData || exporting}>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button variant="outline" onClick={() => handleExportPdf('snap-et-caseload-management')} disabled={exportingPdf}>
                  {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                  Export PDF
                </Button>
              </div>

              {snapEtCaseloadLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : snapEtCaseloadData ? (
                <div className="space-y-6">
                  {/* Executive Summary KPI Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                    <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center text-center">
                          <Users className="w-8 h-8 text-purple-500 mb-2" />
                          <p className="text-xs text-muted-foreground">Active Participants</p>
                          <p className="text-2xl font-bold text-purple-700">{snapEtCaseloadData.summary.total_active_participants}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center text-center">
                          <UserCheck className="w-8 h-8 text-blue-500 mb-2" />
                          <p className="text-xs text-muted-foreground">Active Case Managers</p>
                          <p className="text-2xl font-bold text-blue-700">{snapEtCaseloadData.summary.total_active_case_managers}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center text-center">
                          <TrendingUp className="w-8 h-8 text-green-500 mb-2" />
                          <p className="text-xs text-muted-foreground">Avg Caseload</p>
                          <p className="text-2xl font-bold text-green-700">{snapEtCaseloadData.summary.average_caseload_per_manager}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-indigo-50 to-white border-indigo-200">
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center text-center">
                          <FileText className="w-8 h-8 text-indigo-500 mb-2" />
                          <p className="text-xs text-muted-foreground">Open Cases</p>
                          <p className="text-2xl font-bold text-indigo-700">{snapEtCaseloadData.summary.total_open_cases}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-gray-50 to-white border-gray-200">
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center text-center">
                          <ClipboardList className="w-8 h-8 text-gray-500 mb-2" />
                          <p className="text-xs text-muted-foreground">Closed (Period)</p>
                          <p className="text-2xl font-bold text-gray-700">{snapEtCaseloadData.summary.total_closed_cases_period}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-teal-50 to-white border-teal-200">
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center text-center">
                          <Target className="w-8 h-8 text-teal-500 mb-2" />
                          <p className="text-xs text-muted-foreground">New Enrollments</p>
                          <p className="text-2xl font-bold text-teal-700">{snapEtCaseloadData.summary.total_new_enrollments_period}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-200">
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center text-center">
                          <Activity className="w-8 h-8 text-orange-500 mb-2" />
                          <p className="text-xs text-muted-foreground">No Activity (30d)</p>
                          <p className="text-2xl font-bold text-orange-700">{snapEtCaseloadData.summary.participants_without_activity_30_days}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Case Manager Caseload Dashboard */}
                  <Card className="card-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-primary" />
                        Case Manager Caseload Dashboard
                      </CardTitle>
                      <CardDescription>
                        Click on a case manager to view their current caseload
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {snapEtCaseloadData.case_managers.length > 0 ? (
                        <div className="space-y-4">
                          {snapEtCaseloadData.case_managers.map((cm) => (
                            <Card key={cm.id} className="border hover:border-primary/50 transition-colors">
                              <CardHeader 
                                className="cursor-pointer py-4"
                                onClick={() => toggleCaseManagerExpanded(cm.id)}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                      <UserCheck className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                      <h4 className="font-semibold">{cm.name}</h4>
                                      <p className="text-sm text-muted-foreground">{cm.job_title} • {cm.office_location}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-6">
                                    <div className="text-center">
                                      <p className="text-sm text-muted-foreground">Active Participants</p>
                                      <p className="text-xl font-bold text-purple-600">{cm.total_active_participants}</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-sm text-muted-foreground">Open Cases</p>
                                      <p className="text-xl font-bold text-indigo-600">{cm.open_cases}</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-sm text-muted-foreground">Closed (Period)</p>
                                      <p className="text-xl font-bold text-gray-600">{cm.closed_cases_period}</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-sm text-muted-foreground">% of Total</p>
                                      <p className="text-xl font-bold text-green-600">{cm.percentage_of_total}%</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-sm text-muted-foreground">Last Activity</p>
                                      <p className="text-sm font-medium">{cm.last_participant_activity}</p>
                                    </div>
                                    {expandedCaseManagers[cm.id] ? (
                                      <ChevronUp className="w-5 h-5 text-muted-foreground" />
                                    ) : (
                                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                                    )}
                                  </div>
                                </div>
                              </CardHeader>
                              
                              {expandedCaseManagers[cm.id] && cm.caseload.length > 0 && (
                                <CardContent className="pt-0">
                                  <div className="rounded-md border">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>Participant</TableHead>
                                          <TableHead>Phone</TableHead>
                                          <TableHead>Program</TableHead>
                                          <TableHead>State</TableHead>
                                          <TableHead>Start Date</TableHead>
                                          <TableHead>Last Activity</TableHead>
                                          <TableHead>Priority</TableHead>
                                          <TableHead>Action</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {cm.caseload.map((caseItem) => (
                                          <TableRow key={caseItem.case_id}>
                                            <TableCell className="font-medium">{caseItem.participant_name}</TableCell>
                                            <TableCell>{caseItem.participant_phone || '-'}</TableCell>
                                            <TableCell>{caseItem.program_name}</TableCell>
                                            <TableCell>{caseItem.state}</TableCell>
                                            <TableCell>{caseItem.start_date}</TableCell>
                                            <TableCell>
                                              <Badge variant={caseItem.last_activity === 'No activity' ? 'destructive' : 'outline'}>
                                                {caseItem.last_activity}
                                              </Badge>
                                            </TableCell>
                                            <TableCell>
                                              <Badge variant={
                                                caseItem.priority === 'high' ? 'destructive' : 
                                                caseItem.priority === 'medium' ? 'warning' : 'secondary'
                                              }>
                                                {caseItem.priority}
                                              </Badge>
                                            </TableCell>
                                            <TableCell>
                                              <div className="flex gap-2">
                                                <Link to={`/cases/${caseItem.case_id}`}>
                                                  <Button variant="outline" size="sm">
                                                    <Eye className="w-4 h-4 mr-1" />
                                                    View Case
                                                  </Button>
                                                </Link>
                                                <Button 
                                                  variant="outline" 
                                                  size="sm"
                                                  onClick={() => openReassignDialog(caseItem, cm.name)}
                                                  data-testid={`reassign-case-${caseItem.case_id}`}
                                                >
                                                  <UserCog className="w-4 h-4 mr-1" />
                                                  Reassign
                                                </Button>
                                              </div>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </CardContent>
                              )}
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No case managers found with SNAP E&T cases</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card className="card-shadow">
                  <CardContent className="py-12 text-center">
                    <Briefcase className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-semibold">SNAP E&amp;T Caseload Management Report</h3>
                    <p className="text-muted-foreground mt-2">Click &quot;Apply Filters&quot; to generate the report</p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* SNAP Outreach Report */}
        <TabsContent value="snap-outreach" className="space-y-6" data-testid="snap-outreach-tab">
          {!canViewSnapOutreach ? (
            <Card className="card-shadow">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center justify-center py-12">
                  <Lock className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">Access Restricted</h3>
                  <p className="text-muted-foreground">This report is only available to System Admin, Executive, and Operations Manager roles.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Filters */}
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    SNAP Outreach Report Filters
                  </CardTitle>
                  <CardDescription>Generate a report for SNAP Outreach services within a date range</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                    <div className="space-y-2">
                      <Label>Enrollment Type</Label>
                      <Select
                        value={snapOutreachFilters.enrollment_type_id}
                        onValueChange={(value) => setSnapOutreachFilters({...snapOutreachFilters, enrollment_type_id: value})}
                      >
                        <SelectTrigger data-testid="snap-outreach-enrollment-type-filter">
                          <SelectValue placeholder="All Enrollment Types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">All Enrollment Types</SelectItem>
                          {(snapOutreachEnrollmentTypes.length > 0 ? snapOutreachEnrollmentTypes : [
                            {id: 'snap_outreach_nc', name: 'SNAP Outreach - North Carolina'},
                            {id: 'snap_outreach_tn', name: 'SNAP Outreach - Tennessee'}
                          ]).map(et => (
                            <SelectItem key={et.id} value={et.id}>{et.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Select
                        value={snapOutreachFilters.state}
                        onValueChange={(value) => setSnapOutreachFilters({...snapOutreachFilters, state: value})}
                      >
                        <SelectTrigger data-testid="snap-outreach-state-filter">
                          <SelectValue placeholder="All States" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">All States</SelectItem>
                          <SelectItem value="NC">North Carolina</SelectItem>
                          <SelectItem value="TN">Tennessee</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={snapOutreachFilters.start_date}
                        onChange={(e) => setSnapOutreachFilters({...snapOutreachFilters, start_date: e.target.value, current_month: false, current_fiscal_year: false})}
                        data-testid="snap-outreach-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={snapOutreachFilters.end_date}
                        onChange={(e) => setSnapOutreachFilters({...snapOutreachFilters, end_date: e.target.value, current_month: false, current_fiscal_year: false})}
                        data-testid="snap-outreach-end-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>&nbsp;</Label>
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={handleSnapOutreachCurrentMonth}
                        data-testid="snap-outreach-current-month-btn"
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Current Month
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Label>&nbsp;</Label>
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={handleSnapOutreachCurrentFiscalYear}
                        data-testid="snap-outreach-fiscal-year-btn"
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Current Fiscal Year
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Label>&nbsp;</Label>
                      <Button 
                        className="w-full"
                        onClick={handleSnapOutreachGenerateReport}
                        disabled={snapOutreachLoading || (!snapOutreachFilters.start_date && !snapOutreachFilters.current_month)}
                        data-testid="snap-outreach-generate-btn"
                      >
                        {snapOutreachLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <BarChart3 className="w-4 h-4 mr-2" />
                        )}
                        Generate Report
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Report Results */}
              {snapOutreachData && (
                <>
                  {/* Export Buttons Header */}
                  <Card className="card-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-purple-600" />
                            SNAP Outreach Report Results
                          </CardTitle>
                          <CardDescription>
                            {snapOutreachFilters.start_date} to {snapOutreachFilters.end_date}
                            {snapOutreachFilters.state !== '_all' && ` • ${snapOutreachFilters.state}`}
                          </CardDescription>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            onClick={handleSnapOutreachExportPdf}
                            disabled={snapOutreachExportingPdf}
                            data-testid="snap-outreach-export-pdf-btn"
                          >
                            {snapOutreachExportingPdf ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <FileDown className="w-4 h-4 mr-2" />
                            )}
                            Export PDF
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handleSnapOutreachExportExcel}
                            disabled={snapOutreachExportingExcel}
                            data-testid="snap-outreach-export-excel-btn"
                          >
                            {snapOutreachExportingExcel ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4 mr-2" />
                            )}
                            Export Excel
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="card-shadow border-blue-200 dark:border-blue-800">
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                            <FileText className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Information Dissemination</p>
                            <p className="text-3xl font-bold text-blue-600">{snapOutreachData.summary.information_dissemination.count}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="card-shadow border-green-200 dark:border-green-800 cursor-pointer hover:shadow-md transition-shadow" data-testid="snap-app-assistance-card"
                      onClick={() => document.getElementById('snap-app-assistance-details')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                            <UserCheck className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Application Assistance</p>
                            <p className="text-3xl font-bold text-green-600" data-testid="snap-app-assistance-count">{snapOutreachData.summary.application_assistance.count}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="card-shadow border-purple-200 dark:border-purple-800">
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                            <Activity className="w-6 h-6 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Total SNAP Outreach Services</p>
                            <p className="text-3xl font-bold text-purple-600">{snapOutreachData.summary.total_services?.count || 0}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* SNAP Benefits Status Section */}
                  <Card className="card-shadow">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Heart className="w-5 h-5 text-red-500" />
                        SNAP Benefits Status
                      </CardTitle>
                      <CardDescription>
                        Breakdown of participants receiving or not receiving SNAP benefits
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex items-center gap-4 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                          <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                            <Check className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Receiving SNAP</p>
                            <p className="text-2xl font-bold text-green-600" data-testid="snap-benefits-receiving">
                              {snapOutreachData.summary.snap_benefits_status?.receiving_snap || 0}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 p-4 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800">
                          <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center">
                            <X className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Not Receiving SNAP</p>
                            <p className="text-2xl font-bold text-orange-600" data-testid="snap-benefits-not-receiving">
                              {snapOutreachData.summary.snap_benefits_status?.not_receiving_snap || 0}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700">
                          <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                            <Users className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Total Participants</p>
                            <p className="text-2xl font-bold text-gray-700 dark:text-gray-300" data-testid="snap-benefits-total">
                              {snapOutreachData.summary.snap_benefits_status?.total_participants || 0}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Appointments Breakdown Section */}
                  <Card className="card-shadow">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-indigo-500" />
                        SNAP Application Assistance Appointments
                      </CardTitle>
                      <CardDescription>
                        Breakdown of SNAP Application Assistance appointment statuses
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="flex flex-col items-center p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                          <p className="text-sm text-muted-foreground mb-1">Scheduled</p>
                          <p className="text-2xl font-bold text-blue-600" data-testid="appointments-scheduled">
                            {snapOutreachData.summary.appointments?.by_status?.Scheduled || 0}
                          </p>
                        </div>
                        <div className="flex flex-col items-center p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                          <p className="text-sm text-muted-foreground mb-1">Attended</p>
                          <p className="text-2xl font-bold text-green-600" data-testid="appointments-attended">
                            {snapOutreachData.summary.appointments?.by_status?.Attended || 0}
                          </p>
                        </div>
                        <div className="flex flex-col items-center p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                          <p className="text-sm text-muted-foreground mb-1">Rescheduled</p>
                          <p className="text-2xl font-bold text-yellow-600" data-testid="appointments-rescheduled">
                            {snapOutreachData.summary.appointments?.by_status?.Rescheduled || 0}
                          </p>
                        </div>
                        <div className="flex flex-col items-center p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                          <p className="text-sm text-muted-foreground mb-1">No Show</p>
                          <p className="text-2xl font-bold text-red-600" data-testid="appointments-no-show">
                            {snapOutreachData.summary.appointments?.by_status?.["No Show"] || 0}
                          </p>
                        </div>
                        <div className="flex flex-col items-center p-4 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800">
                          <p className="text-sm text-muted-foreground mb-1">Total</p>
                          <p className="text-2xl font-bold text-indigo-600" data-testid="appointments-total">
                            {snapOutreachData.summary.appointments?.total || 0}
                          </p>
                        </div>
                      </div>
                      
                      {/* Detailed Appointments List */}
                      {snapOutreachData.details?.appointments?.length > 0 && (
                        <div className="mt-6">
                          <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <ClipboardList className="w-4 h-4 text-indigo-600" />
                            Appointment Details ({snapOutreachData.details.appointments.length})
                          </h4>
                          <div className="rounded-md border overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Participant Name</TableHead>
                                  <TableHead>Appointment Type</TableHead>
                                  <TableHead>Date</TableHead>
                                  <TableHead>Time</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead>Assigned Staff</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {snapOutreachData.details.appointments.map((apt, idx) => (
                                  <TableRow key={apt.id || idx} data-testid={`appointment-row-${idx}`}>
                                    <TableCell className="font-medium">{apt.participant_name}</TableCell>
                                    <TableCell>{apt.appointment_type}</TableCell>
                                    <TableCell>{formatDateUtil(apt.date)}</TableCell>
                                    <TableCell>{formatTime12(apt.time) || '-'}</TableCell>
                                    <TableCell>
                                      <Badge 
                                        variant="outline" 
                                        className={
                                          apt.status === 'Attended' ? 'bg-green-100 text-green-700 border-green-300' :
                                          apt.status === 'No Show' ? 'bg-red-100 text-red-700 border-red-300' :
                                          apt.status === 'Rescheduled' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                                          'bg-blue-100 text-blue-700 border-blue-300'
                                        }
                                      >
                                        {apt.status}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>{apt.assigned_staff}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}
                      
                      {snapOutreachData.summary.appointments?.total === 0 && (
                        <div className="mt-6 text-center py-6 text-muted-foreground">
                          <Calendar className="w-10 h-10 mx-auto mb-3 opacity-50" />
                          <p>No appointments recorded for the selected date range.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Report Details */}
                  <Card className="card-shadow">
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <div>
                          <CardTitle>SNAP Outreach Report</CardTitle>
                          <CardDescription>
                            State: {snapOutreachData.state} | 
                            Period: {formatDateUtil(snapOutreachData.date_range.start_date)} to {formatDateUtil(snapOutreachData.date_range.end_date)} |
                            Generated: {formatDateTime(snapOutreachData.generated_at)}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Information Dissemination Details */}
                      {snapOutreachData.details.information_dissemination_notes.length > 0 && (
                        <div className="mb-8">
                          <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-600" />
                            Information Dissemination ({snapOutreachData.summary.information_dissemination.count})
                          </h4>
                          <div className="rounded-md border overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Date</TableHead>
                                  <TableHead>Participant Name</TableHead>
                                  <TableHead>Case ID</TableHead>
                                  <TableHead>Subject</TableHead>
                                  <TableHead>Author</TableHead>
                                  <TableHead>Action</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {snapOutreachData.details.information_dissemination_notes.map((note) => (
                                  <TableRow key={note.id}>
                                    <TableCell>{note.date_of_service}</TableCell>
                                    <TableCell>{note.participant_name || '-'}</TableCell>
                                    <TableCell className="font-mono text-sm">{note.case_id}</TableCell>
                                    <TableCell data-testid={`snap-info-diss-subject-${note.id}`}>{note.subject_type || note.subject}</TableCell>
                                    <TableCell>{note.author_name}</TableCell>
                                    <TableCell>
                                      <Link to={`/cases/${note.case_id}`}>
                                        <Button variant="outline" size="sm">
                                          <Eye className="w-4 h-4 mr-1" />
                                          View
                                        </Button>
                                      </Link>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}

                      {/* Application Assistance Details */}
                      {snapOutreachData.details.application_assistance_notes.length > 0 && (
                        <div id="snap-app-assistance-details" className="scroll-mt-24" data-testid="snap-app-assistance-details">
                          <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <UserCheck className="w-4 h-4 text-green-600" />
                            Application Assistance ({snapOutreachData.summary.application_assistance.count})
                          </h4>
                          <div className="rounded-md border overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Date</TableHead>
                                  <TableHead>Participant Name</TableHead>
                                  <TableHead>Case ID</TableHead>
                                  <TableHead>Subject</TableHead>
                                  <TableHead>Author</TableHead>
                                  <TableHead>Action</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {snapOutreachData.details.application_assistance_notes.map((note) => (
                                  <TableRow key={note.id}>
                                    <TableCell>{note.date_of_service}</TableCell>
                                    <TableCell>{note.participant_name || '-'}</TableCell>
                                    <TableCell className="font-mono text-sm">{note.case_id}</TableCell>
                                    <TableCell>{note.subject}</TableCell>
                                    <TableCell>{note.author_name}</TableCell>
                                    <TableCell>
                                      <Link to={`/cases/${note.case_id}`}>
                                        <Button variant="outline" size="sm">
                                          <Eye className="w-4 h-4 mr-1" />
                                          View
                                        </Button>
                                      </Link>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}

                      {(snapOutreachData.summary.total_services?.count || 0) === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>No SNAP Outreach services recorded for the selected date range.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}

              {!snapOutreachData && !snapOutreachLoading && (
                <Card className="card-shadow">
                  <CardContent className="pt-6">
                    <div className="text-center py-12 text-muted-foreground">
                      <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Select a date range and click &quot;Generate Report&quot; to view SNAP Outreach metrics.</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* SNAP Outreach Enrollment List */}
              <Card className="card-shadow mt-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-green-600" />
                    SNAP Outreach Enrollment List
                  </CardTitle>
                  <CardDescription>View all SNAP Outreach cases within a date range, filtered by case open date</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Enrollment List Filters */}
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Select
                        value={snapEnrollmentListFilters.state}
                        onValueChange={(value) => setSnapEnrollmentListFilters({...snapEnrollmentListFilters, state: value})}
                      >
                        <SelectTrigger data-testid="snap-enrollment-list-state-filter">
                          <SelectValue placeholder="All States" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">All States</SelectItem>
                          <SelectItem value="NC">North Carolina</SelectItem>
                          <SelectItem value="TN">Tennessee</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={snapEnrollmentListFilters.start_date}
                        onChange={(e) => setSnapEnrollmentListFilters({...snapEnrollmentListFilters, start_date: e.target.value, current_month: false, current_fiscal_year: false})}
                        data-testid="snap-enrollment-list-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={snapEnrollmentListFilters.end_date}
                        onChange={(e) => setSnapEnrollmentListFilters({...snapEnrollmentListFilters, end_date: e.target.value, current_month: false, current_fiscal_year: false})}
                        data-testid="snap-enrollment-list-end-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>&nbsp;</Label>
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={handleSnapEnrollmentListCurrentMonth}
                        data-testid="snap-enrollment-list-current-month-btn"
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Current Month
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Label>&nbsp;</Label>
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={handleSnapEnrollmentListCurrentFiscalYear}
                        data-testid="snap-enrollment-list-fiscal-year-btn"
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Current Fiscal Year
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Label>&nbsp;</Label>
                      <Button 
                        className="w-full"
                        onClick={fetchSnapEnrollmentList}
                        disabled={snapEnrollmentListLoading || !snapEnrollmentListFilters.start_date || !snapEnrollmentListFilters.end_date}
                        data-testid="snap-enrollment-list-generate-btn"
                      >
                        {snapEnrollmentListLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <BarChart3 className="w-4 h-4 mr-2" />
                        )}
                        Generate List
                      </Button>
                    </div>
                  </div>

                  {/* Enrollment List Results */}
                  {snapEnrollmentListData && (
                    <div className="mt-6 space-y-4">
                      {/* Summary */}
                      <div className="flex items-center gap-4 text-sm">
                        <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50">
                          Total: {snapEnrollmentListData.summary?.total_cases || 0}
                        </Badge>
                        <Badge variant="outline" className="text-blue-700 border-blue-300 bg-blue-50">
                          Open: {snapEnrollmentListData.summary?.open_cases || 0}
                        </Badge>
                        <Badge variant="outline" className="text-gray-700 border-gray-300 bg-gray-50">
                          Closed: {snapEnrollmentListData.summary?.closed_cases || 0}
                        </Badge>
                      </div>

                      {/* Enrollments Table */}
                      {snapEnrollmentListData.enrollments?.length > 0 ? (
                        <div className="rounded-md border overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Participant Name</TableHead>
                                <TableHead>Case Open Date</TableHead>
                                <TableHead>Case Close Date</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Action</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {snapEnrollmentListData.enrollments.map((enrollment) => (
                                <TableRow key={enrollment.case_id}>
                                  <TableCell className="font-medium">{enrollment.participant_name || '-'}</TableCell>
                                  <TableCell>{enrollment.case_open_date || '-'}</TableCell>
                                  <TableCell>{enrollment.case_close_date || '-'}</TableCell>
                                  <TableCell>
                                    <Badge 
                                      variant={enrollment.status?.toLowerCase() === 'closed' ? 'secondary' : 'default'}
                                      className={enrollment.status?.toLowerCase() === 'closed' ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-700'}
                                    >
                                      {enrollment.status || 'Unknown'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <Link to={`/cases/${enrollment.case_id}`}>
                                      <Button variant="outline" size="sm">
                                        <Eye className="w-4 h-4 mr-1" />
                                        View Case
                                      </Button>
                                    </Link>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>No SNAP Outreach enrollments found for the selected date range.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {!snapEnrollmentListData && !snapEnrollmentListLoading && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Select a date range and click &quot;Generate List&quot; to view SNAP Outreach enrollments.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Pathways Module Completion Report */}
        <TabsContent value="pathways-modules" className="space-y-6" data-testid="pathways-modules-tab">
          {!canViewSnapOutreach ? (
            <Card className="card-shadow">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center justify-center py-12">
                  <Lock className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">Access Restricted</h3>
                  <p className="text-muted-foreground">This report is only available to System Admin, Executive, and Operations Manager roles.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Filters */}
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    Pathways to Employability Module Completion Report
                  </CardTitle>
                  <CardDescription>Track completion of Pathways to Employability training modules across SNAP E&T participants</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={pathwaysFilters.start_date}
                        onChange={(e) => setPathwaysFilters({...pathwaysFilters, start_date: e.target.value})}
                        data-testid="pathways-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={pathwaysFilters.end_date}
                        onChange={(e) => setPathwaysFilters({...pathwaysFilters, end_date: e.target.value})}
                        data-testid="pathways-end-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>&nbsp;</Label>
                      <Button 
                        className="w-full"
                        onClick={fetchPathwaysModuleReport}
                        disabled={pathwaysLoading || !pathwaysFilters.start_date || !pathwaysFilters.end_date}
                        data-testid="pathways-generate-btn"
                      >
                        {pathwaysLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <BarChart3 className="w-4 h-4 mr-2" />
                        )}
                        Generate Report
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Label>&nbsp;</Label>
                      <Button 
                        variant="outline"
                        className="w-full"
                        onClick={exportPathwaysReportCsv}
                        disabled={!pathwaysData}
                        data-testid="pathways-export-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export CSV
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Report Results */}
              {pathwaysData && (
                <>
                  {/* Summary Card */}
                  <Card className="card-shadow border-purple-200 dark:border-purple-800">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                          <TrendingUp className="w-7 h-7 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Total Module Completions</p>
                          <p className="text-4xl font-bold text-purple-600">{pathwaysData.total_completions}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {pathwaysFilters.start_date} to {pathwaysFilters.end_date}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Module Breakdown */}
                  <Card className="card-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">Module Completion Breakdown</CardTitle>
                      <CardDescription>Click on a module to view individual completions</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {Object.entries(pathwaysData.modules).map(([moduleName, moduleData]) => (
                          <div key={moduleName} className="border rounded-lg overflow-hidden">
                            <button
                              className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
                              onClick={() => toggleModuleExpanded(moduleName)}
                              data-testid={`module-toggle-${moduleName.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${moduleData.count > 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                                  <span className={`font-bold ${moduleData.count > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                    {moduleData.count}
                                  </span>
                                </div>
                                <span className="font-medium text-left">{moduleName}</span>
                              </div>
                              {expandedModules[moduleName] ? (
                                <ChevronUp className="w-5 h-5 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-muted-foreground" />
                              )}
                            </button>
                            
                            {expandedModules[moduleName] && (
                              <div className="p-4 border-t">
                                {moduleData.completions.length > 0 ? (
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Participant Name</TableHead>
                                        <TableHead>Completion Date</TableHead>
                                        <TableHead>Case ID</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {moduleData.completions.map((completion, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell className="font-medium">{completion.participant_name}</TableCell>
                                          <TableCell>{formatDateUtil(completion.completion_date)}</TableCell>
                                          <TableCell>
                                            <Badge variant="outline">{completion.case_id}</Badge>
                                          </TableCell>
                                          <TableCell className="text-right">
                                            <Link to={`/cases/${completion.case_id}`}>
                                              <Button variant="ghost" size="sm">
                                                <Eye className="w-4 h-4 mr-1" />
                                                View Case
                                              </Button>
                                            </Link>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                ) : (
                                  <p className="text-center text-muted-foreground py-4">No completions recorded for this module in the selected date range.</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Chart View */}
                  <Card className="card-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">Completions by Module</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart 
                          data={Object.entries(pathwaysData.modules).map(([name, data]) => ({ 
                            name: name.length > 20 ? name.substring(0, 20) + '...' : name, 
                            fullName: name,
                            count: data.count 
                          }))}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" width={180} tick={{ fontSize: 11 }} />
                          <Tooltip 
                            formatter={(value, name, props) => [value, 'Completions']}
                            labelFormatter={(label, payload) => payload[0]?.payload?.fullName || label}
                          />
                          <Bar dataKey="count" fill="#92298d" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </>
              )}

              {!pathwaysData && !pathwaysLoading && (
                <Card className="card-shadow">
                  <CardContent className="pt-6">
                    <div className="text-center py-12 text-muted-foreground">
                      <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Select a date range and click &quot;Generate Report&quot; to view Pathways module completion data.</p>
                      <p className="text-sm mt-2">This report tracks completions of the 8 Pathways to Employability training modules.</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* HELP Institute Course Completion Report */}
        <TabsContent value="help-institute-courses" className="space-y-6" data-testid="help-institute-courses-tab">
          {!canViewSnapOutreach ? (
            <Card className="card-shadow">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center justify-center py-12">
                  <Lock className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">Access Restricted</h3>
                  <p className="text-muted-foreground">This report is only available to System Admin, Executive, and Operations Manager roles.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Filters */}
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5" />
                    HELP Institute Course Completion Report
                  </CardTitle>
                  <CardDescription>Track completion of HELP Institute courses across enrolled participants</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={helpInstituteFilters.start_date}
                        onChange={(e) => setHelpInstituteFilters({...helpInstituteFilters, start_date: e.target.value})}
                        data-testid="help-institute-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={helpInstituteFilters.end_date}
                        onChange={(e) => setHelpInstituteFilters({...helpInstituteFilters, end_date: e.target.value})}
                        data-testid="help-institute-end-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>&nbsp;</Label>
                      <Button 
                        className="w-full"
                        onClick={fetchHelpInstituteReport}
                        disabled={helpInstituteLoading || !helpInstituteFilters.start_date || !helpInstituteFilters.end_date}
                        data-testid="help-institute-generate-btn"
                      >
                        {helpInstituteLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <BarChart3 className="w-4 h-4 mr-2" />
                        )}
                        Generate Report
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Label>&nbsp;</Label>
                      <Button 
                        variant="outline"
                        className="w-full"
                        onClick={exportHelpInstituteReportCsv}
                        disabled={!helpInstituteData}
                        data-testid="help-institute-export-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export CSV
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Report Results */}
              {helpInstituteData && (
                <>
                  {/* Summary Card */}
                  <Card className="card-shadow border-purple-200 dark:border-purple-800">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                          <GraduationCap className="w-7 h-7 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Total Course Completions</p>
                          <p className="text-4xl font-bold text-purple-600">{helpInstituteData.total_completions}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {helpInstituteFilters.start_date} to {helpInstituteFilters.end_date}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Course Breakdown */}
                  <Card className="card-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">Course Completion Breakdown</CardTitle>
                      <CardDescription>Click on a course to view individual completions</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {Object.entries(helpInstituteData.courses).map(([courseName, courseData]) => (
                          <div key={courseName} className="border rounded-lg overflow-hidden">
                            <button
                              className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
                              onClick={() => toggleCourseExpanded(courseName)}
                              data-testid={`course-toggle-${courseName.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${courseData.count > 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                                  <span className={`font-bold ${courseData.count > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                    {courseData.count}
                                  </span>
                                </div>
                                <span className="font-medium text-left">{courseName}</span>
                              </div>
                              {expandedCourses[courseName] ? (
                                <ChevronUp className="w-5 h-5 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-muted-foreground" />
                              )}
                            </button>
                            
                            {expandedCourses[courseName] && (
                              <div className="p-4 border-t">
                                {courseData.completions.length > 0 ? (
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Participant Name</TableHead>
                                        <TableHead>Completion Date</TableHead>
                                        <TableHead>Case ID</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {courseData.completions.map((completion, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell className="font-medium">{completion.participant_name}</TableCell>
                                          <TableCell>{formatDateUtil(completion.completion_date)}</TableCell>
                                          <TableCell>
                                            <Badge variant="outline">{completion.case_id}</Badge>
                                          </TableCell>
                                          <TableCell className="text-right">
                                            <Link to={`/cases/${completion.case_id}`}>
                                              <Button variant="ghost" size="sm">
                                                <Eye className="w-4 h-4 mr-1" />
                                                View Case
                                              </Button>
                                            </Link>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                ) : (
                                  <p className="text-center text-muted-foreground py-4">No completions recorded for this course in the selected date range.</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Chart View */}
                  <Card className="card-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">Completions by Course</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart 
                          data={Object.entries(helpInstituteData.courses).map(([name, data]) => ({ 
                            name: name.length > 20 ? name.substring(0, 20) + '...' : name, 
                            fullName: name,
                            count: data.count 
                          }))}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" width={180} tick={{ fontSize: 11 }} />
                          <Tooltip 
                            formatter={(value, name, props) => [value, 'Completions']}
                            labelFormatter={(label, payload) => payload[0]?.payload?.fullName || label}
                          />
                          <Bar dataKey="count" fill="#92298d" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </>
              )}

              {!helpInstituteData && !helpInstituteLoading && (
                <Card className="card-shadow">
                  <CardContent className="pt-6">
                    <div className="text-center py-12 text-muted-foreground">
                      <GraduationCap className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Select a date range and click &quot;Generate Report&quot; to view HELP Institute course completion data.</p>
                      <p className="text-sm mt-2">This report tracks completions of The HELP Institute courses across SNAP E&amp;T participants.</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Participant Costs Report */}
        <TabsContent value="participant-costs" className="space-y-6" data-testid="participant-costs-tab">
          {!canViewSnapOutreach ? (
            <Card className="card-shadow">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center justify-center py-12">
                  <Lock className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">Access Restricted</h3>
                  <p className="text-muted-foreground">This report is only available to System Admin, Executive, and Operations Manager roles.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Filters */}
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    Participant Costs Report
                  </CardTitle>
                  <CardDescription>Track and analyze participant-related program costs across SNAP E&T cases</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={participantCostsFilters.start_date}
                        onChange={(e) => setParticipantCostsFilters({...participantCostsFilters, start_date: e.target.value})}
                        data-testid="costs-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={participantCostsFilters.end_date}
                        onChange={(e) => setParticipantCostsFilters({...participantCostsFilters, end_date: e.target.value})}
                        data-testid="costs-end-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Select value={participantCostsFilters.state} onValueChange={(v) => setParticipantCostsFilters({...participantCostsFilters, state: v})}>
                        <SelectTrigger data-testid="costs-state-filter">
                          <SelectValue placeholder="All States" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">All States</SelectItem>
                          <SelectItem value="TN">Tennessee</SelectItem>
                          <SelectItem value="NC">North Carolina</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>&nbsp;</Label>
                      <Button 
                        className="w-full"
                        onClick={fetchParticipantCostsReport}
                        disabled={participantCostsLoading || !participantCostsFilters.start_date || !participantCostsFilters.end_date}
                        data-testid="costs-generate-btn"
                      >
                        {participantCostsLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <BarChart3 className="w-4 h-4 mr-2" />
                        )}
                        Generate Report
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Label>&nbsp;</Label>
                      <Button 
                        variant="outline"
                        className="w-full"
                        onClick={exportParticipantCostsReportCsv}
                        disabled={!participantCostsData}
                        data-testid="costs-export-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export CSV
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Report Results */}
              {participantCostsData && (
                <>
                  {/* Grand Total Summary */}
                  <Card className="card-shadow border-green-200 dark:border-green-800">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                          <DollarSign className="w-7 h-7 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Grand Total Participant Costs</p>
                          <p className="text-4xl font-bold text-green-600">
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(participantCostsData.summary?.grand_total || 0)}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {participantCostsData.summary?.total_transactions || 0} transaction(s) | {participantCostsFilters.start_date} to {participantCostsFilters.end_date}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Category Summary */}
                  <Card className="card-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">Summary by Category</CardTitle>
                      <CardDescription>Click on a category to view billing item breakdown</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {participantCostsData.summary?.categories?.map(cat => (
                          <div key={cat.category_id} className="border rounded-lg overflow-hidden">
                            <button
                              className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
                              onClick={() => toggleCostCategoryExpanded(cat.category_id)}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cat.total > 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                                  <span className={`font-bold text-sm ${cat.total > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                    {cat.transaction_count}
                                  </span>
                                </div>
                                <span className="font-medium">{cat.category_name}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="font-semibold text-green-600">
                                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cat.total || 0)}
                                </span>
                                {expandedCostCategories[cat.category_id] ? (
                                  <ChevronUp className="w-5 h-5 text-muted-foreground" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                                )}
                              </div>
                            </button>
                            
                            {expandedCostCategories[cat.category_id] && (
                              <div className="p-4 border-t bg-background">
                                {cat.billing_items?.length > 0 ? (
                                  <div className="space-y-2">
                                    {cat.billing_items.map(item => (
                                      <div key={item.id} className="flex justify-between text-sm pl-4">
                                        <span className="text-muted-foreground">{item.name}</span>
                                        <span className="font-medium">
                                          {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.total || 0)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-muted-foreground text-center py-2">No billing items with transactions in this category</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Itemized Transaction List */}
                  <Card className="card-shadow">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Receipt className="w-5 h-5" />
                            Itemized Transaction List
                          </CardTitle>
                          <CardDescription>
                            All qualifying transactions for the selected date range ({participantCostsData.transactions?.length || 0} transactions)
                          </CardDescription>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setShowItemizedTransactions(!showItemizedTransactions)}
                        >
                          {showItemizedTransactions ? 'Hide' : 'Show'} Transactions
                        </Button>
                      </div>
                    </CardHeader>
                    {showItemizedTransactions && (
                      <CardContent>
                        {participantCostsData.transactions?.length > 0 ? (
                          <div className="rounded-md border overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Participant</TableHead>
                                  <TableHead>Case</TableHead>
                                  <TableHead>Date</TableHead>
                                  <TableHead>Category</TableHead>
                                  <TableHead>Billing Item</TableHead>
                                  <TableHead>Description</TableHead>
                                  <TableHead className="text-right">Qty</TableHead>
                                  <TableHead className="text-right">Unit Cost</TableHead>
                                  <TableHead className="text-right">Total</TableHead>
                                  <TableHead>Contract</TableHead>
                                  <TableHead>Ref #</TableHead>
                                  <TableHead>Status</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {participantCostsData.transactions.map(t => (
                                  <TableRow key={t.id}>
                                    <TableCell className="font-medium">{t.participant_name}</TableCell>
                                    <TableCell>
                                      <Link to={`/cases/${t.case_id}`} className="text-purple-600 hover:underline">
                                        {t.case_id?.substring(0, 12)}...
                                      </Link>
                                    </TableCell>
                                    <TableCell>{formatDateUtil(t.transaction_date)}</TableCell>
                                    <TableCell>{t.category_name}</TableCell>
                                    <TableCell>{t.billing_item_name}</TableCell>
                                    <TableCell className="max-w-[150px] truncate">{t.description || '-'}</TableCell>
                                    <TableCell className="text-right">{t.quantity}</TableCell>
                                    <TableCell className="text-right">
                                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(t.unit_cost || 0)}
                                    </TableCell>
                                    <TableCell className="text-right font-medium">
                                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(t.total_amount || 0)}
                                    </TableCell>
                                    <TableCell>{t.contract_name || '-'}</TableCell>
                                    <TableCell>{t.reference_number || '-'}</TableCell>
                                    <TableCell>
                                      <Badge className={t.status === 'posted' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                        {t.status}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-muted-foreground">
                            <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>No transactions found for the selected date range.</p>
                          </div>
                        )}
                      </CardContent>
                    )}
                  </Card>
                </>
              )}

              {!participantCostsData && !participantCostsLoading && (
                <Card className="card-shadow">
                  <CardContent className="pt-6">
                    <div className="text-center py-12 text-muted-foreground">
                      <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Select a date range and click &quot;Generate Report&quot; to view Participant Costs data.</p>
                      <p className="text-sm mt-2">This report shows all posted transactions from SNAP E&T cases.</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Employment Outcomes Report */}
        <TabsContent value="employment-outcomes" className="space-y-6" data-testid="employment-outcomes-tab">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5" />
                Employment Outcomes Report
              </CardTitle>
              <CardDescription>
                Track employment outcomes for SNAP E&T participants across all completed cases
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="space-y-2">
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={employmentOutcomesFilters.start_date}
                    onChange={(e) => setEmploymentOutcomesFilters({
                      ...employmentOutcomesFilters,
                      start_date: e.target.value
                    })}
                    data-testid="employment-outcomes-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date *</Label>
                  <Input
                    type="date"
                    value={employmentOutcomesFilters.end_date}
                    onChange={(e) => setEmploymentOutcomesFilters({
                      ...employmentOutcomesFilters,
                      end_date: e.target.value
                    })}
                    data-testid="employment-outcomes-end-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Select
                    value={employmentOutcomesFilters.states.length === 0 ? '_all' : 
                           employmentOutcomesFilters.states.length === 1 ? employmentOutcomesFilters.states[0] : '_multiple'}
                    onValueChange={(value) => {
                      if (value === '_all') {
                        setEmploymentOutcomesFilters({
                          ...employmentOutcomesFilters,
                          states: []
                        });
                      } else if (value !== '_multiple') {
                        // Toggle state in array
                        const currentStates = [...employmentOutcomesFilters.states];
                        const idx = currentStates.indexOf(value);
                        if (idx === -1) {
                          currentStates.push(value);
                        } else {
                          currentStates.splice(idx, 1);
                        }
                        setEmploymentOutcomesFilters({
                          ...employmentOutcomesFilters,
                          states: currentStates.length === 0 ? [] : currentStates
                        });
                      }
                    }}
                  >
                    <SelectTrigger data-testid="employment-outcomes-state-filter">
                      <SelectValue>
                        {employmentOutcomesFilters.states.length === 0 
                          ? 'All States' 
                          : employmentOutcomesFilters.states.length === 1 
                            ? employmentOutcomesFilters.states[0]
                            : `${employmentOutcomesFilters.states.length} States`}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All States</SelectItem>
                      {(employmentOutcomesData?.filter_options?.states || ['TN', 'NC', 'IA']).map(state => (
                        <SelectItem key={state} value={state}>
                          <div className="flex items-center gap-2">
                            {employmentOutcomesFilters.states.includes(state) && (
                              <Check className="w-4 h-4 text-green-600" />
                            )}
                            <span>{state}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {employmentOutcomesFilters.states.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {employmentOutcomesFilters.states.map(state => (
                        <Badge 
                          key={state} 
                          variant="secondary" 
                          className="text-xs cursor-pointer"
                          onClick={() => {
                            setEmploymentOutcomesFilters({
                              ...employmentOutcomesFilters,
                              states: employmentOutcomesFilters.states.filter(s => s !== state)
                            });
                          }}
                        >
                          {state} ×
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Status at Enrollment</Label>
                  <Select
                    value={employmentOutcomesFilters.employment_status_at_enrollment}
                    onValueChange={(value) => setEmploymentOutcomesFilters({
                      ...employmentOutcomesFilters,
                      employment_status_at_enrollment: value
                    })}
                  >
                    <SelectTrigger data-testid="employment-outcomes-status-enrollment-filter">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All</SelectItem>
                      <SelectItem value="Employed">Employed</SelectItem>
                      <SelectItem value="Self-Employed">Self-Employed</SelectItem>
                      <SelectItem value="Unemployed">Unemployed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Employment Status</Label>
                  <Select
                    value={employmentOutcomesFilters.status}
                    onValueChange={(value) => setEmploymentOutcomesFilters({
                      ...employmentOutcomesFilters,
                      status: value
                    })}
                  >
                    <SelectTrigger data-testid="employment-outcomes-status-filter">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All</SelectItem>
                      <SelectItem value="Full-Time">Full-Time</SelectItem>
                      <SelectItem value="Part-Time">Part-Time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Case Manager</Label>
                  <Select
                    value={employmentOutcomesFilters.case_manager_id}
                    onValueChange={(value) => setEmploymentOutcomesFilters({
                      ...employmentOutcomesFilters,
                      case_manager_id: value
                    })}
                  >
                    <SelectTrigger data-testid="employment-outcomes-case-manager-filter">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Case Managers</SelectItem>
                      {employmentOutcomesData?.filter_options?.case_managers?.map(cm => (
                        <SelectItem key={cm.id} value={cm.id}>{cm.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <Button 
                  onClick={fetchEmploymentOutcomesReport} 
                  disabled={employmentOutcomesLoading}
                  data-testid="generate-employment-outcomes-btn"
                >
                  {employmentOutcomesLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Generate Report
                </Button>
                
                {employmentOutcomesData && (
                  <Button 
                    onClick={exportEmploymentOutcomesReport}
                    variant="outline"
                    disabled={exporting}
                    data-testid="export-employment-outcomes-btn"
                  >
                    {exporting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Export to CSV
                  </Button>
                )}
              </div>

              {employmentOutcomesLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                </div>
              )}

              {employmentOutcomesData && !employmentOutcomesLoading && (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="border-green-200 bg-green-50">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-green-700">Total Employment Outcomes</p>
                            <p className="text-3xl font-bold text-green-800" data-testid="total-employment-outcomes">
                              {employmentOutcomesData.summary.total_employment_outcomes}
                            </p>
                          </div>
                          <UserCheck className="w-10 h-10 text-green-600" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-blue-200 bg-blue-50">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-blue-700">Full-Time Placements</p>
                            <p className="text-3xl font-bold text-blue-800" data-testid="full-time-placements">
                              {employmentOutcomesData.summary.full_time_placements}
                            </p>
                          </div>
                          <Briefcase className="w-10 h-10 text-blue-600" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-purple-200 bg-purple-50">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-purple-700">Part-Time Placements</p>
                            <p className="text-3xl font-bold text-purple-800" data-testid="part-time-placements">
                              {employmentOutcomesData.summary.part_time_placements}
                            </p>
                          </div>
                          <Briefcase className="w-10 h-10 text-purple-600" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Detailed Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Detailed Employment Records</CardTitle>
                      <CardDescription>
                        {employmentOutcomesData.records.length} records found. Click column headers to sort.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {employmentOutcomesData.records.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <UserCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>No employment outcomes found for the selected criteria.</p>
                        </div>
                      ) : (
                        <div className="max-h-[500px] overflow-auto">
                          <Table>
                            <TableHeader className="sticky top-0 bg-white">
                              <TableRow>
                                <TableHead 
                                  className="cursor-pointer hover:bg-muted/50"
                                  onClick={() => sortEmploymentOutcomesData('participant_name')}
                                >
                                  Participant Name
                                  {employmentOutcomesSortConfig.key === 'participant_name' && (
                                    employmentOutcomesSortConfig.direction === 'asc' ? 
                                      <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                      <ChevronDown className="w-4 h-4 inline ml-1" />
                                  )}
                                </TableHead>
                                <TableHead 
                                  className="cursor-pointer hover:bg-muted/50"
                                  onClick={() => sortEmploymentOutcomesData('participant_state')}
                                >
                                  State
                                  {employmentOutcomesSortConfig.key === 'participant_state' && (
                                    employmentOutcomesSortConfig.direction === 'asc' ? 
                                      <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                      <ChevronDown className="w-4 h-4 inline ml-1" />
                                  )}
                                </TableHead>
                                <TableHead>Case ID</TableHead>
                                <TableHead 
                                  className="cursor-pointer hover:bg-muted/50"
                                  onClick={() => sortEmploymentOutcomesData('employment_status_at_enrollment')}
                                >
                                  Status at Enrollment
                                  {employmentOutcomesSortConfig.key === 'employment_status_at_enrollment' && (
                                    employmentOutcomesSortConfig.direction === 'asc' ? 
                                      <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                      <ChevronDown className="w-4 h-4 inline ml-1" />
                                  )}
                                </TableHead>
                                <TableHead 
                                  className="cursor-pointer hover:bg-muted/50"
                                  onClick={() => sortEmploymentOutcomesData('start_date')}
                                >
                                  Start Date
                                  {employmentOutcomesSortConfig.key === 'start_date' && (
                                    employmentOutcomesSortConfig.direction === 'asc' ? 
                                      <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                      <ChevronDown className="w-4 h-4 inline ml-1" />
                                  )}
                                </TableHead>
                                <TableHead 
                                  className="cursor-pointer hover:bg-muted/50"
                                  onClick={() => sortEmploymentOutcomesData('employer_name')}
                                >
                                  Employer Name
                                  {employmentOutcomesSortConfig.key === 'employer_name' && (
                                    employmentOutcomesSortConfig.direction === 'asc' ? 
                                      <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                      <ChevronDown className="w-4 h-4 inline ml-1" />
                                  )}
                                </TableHead>
                                <TableHead 
                                  className="cursor-pointer hover:bg-muted/50 text-right"
                                  onClick={() => sortEmploymentOutcomesData('hourly_wage')}
                                >
                                  Hourly Wage
                                  {employmentOutcomesSortConfig.key === 'hourly_wage' && (
                                    employmentOutcomesSortConfig.direction === 'asc' ? 
                                      <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                      <ChevronDown className="w-4 h-4 inline ml-1" />
                                  )}
                                </TableHead>
                                <TableHead 
                                  className="cursor-pointer hover:bg-muted/50"
                                  onClick={() => sortEmploymentOutcomesData('status')}
                                >
                                  Status
                                  {employmentOutcomesSortConfig.key === 'status' && (
                                    employmentOutcomesSortConfig.direction === 'asc' ? 
                                      <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                      <ChevronDown className="w-4 h-4 inline ml-1" />
                                  )}
                                </TableHead>
                                <TableHead>Employment Status</TableHead>
                                <TableHead 
                                  className="cursor-pointer hover:bg-muted/50"
                                  onClick={() => sortEmploymentOutcomesData('case_manager_name')}
                                >
                                  Case Manager
                                  {employmentOutcomesSortConfig.key === 'case_manager_name' && (
                                    employmentOutcomesSortConfig.direction === 'asc' ? 
                                      <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                      <ChevronDown className="w-4 h-4 inline ml-1" />
                                  )}
                                </TableHead>
                                <TableHead 
                                  className="cursor-pointer hover:bg-muted/50"
                                  onClick={() => sortEmploymentOutcomesData('enrollment_date')}
                                >
                                  Enrollment Date
                                  {employmentOutcomesSortConfig.key === 'enrollment_date' && (
                                    employmentOutcomesSortConfig.direction === 'asc' ? 
                                      <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                      <ChevronDown className="w-4 h-4 inline ml-1" />
                                  )}
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {getSortedEmploymentOutcomesRecords().map((record, idx) => (
                                <TableRow key={idx}>
                                  <TableCell className="font-medium">
                                    <Link to={`/cases/${record.case_id}`} className="text-purple-600 hover:underline">
                                      {record.participant_name}
                                    </Link>
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="outline">{record.participant_state || '-'}</Badge>
                                  </TableCell>
                                  <TableCell>
                                    <Link to={`/cases/${record.case_id}`} className="text-blue-600 hover:underline">
                                      {record.case_id}
                                    </Link>
                                  </TableCell>
                                  <TableCell>{record.employment_status_at_enrollment}</TableCell>
                                  <TableCell>{record.start_date}</TableCell>
                                  <TableCell>{record.employer_name}</TableCell>
                                  <TableCell className="text-right">
                                    {record.hourly_wage !== null ? `$${record.hourly_wage.toFixed(2)}` : '-'}
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant={record.status === 'Full-Time' ? 'default' : 'secondary'}>
                                      {record.status}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className="border-green-500 text-green-700">
                                      {record.employment_status}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>{record.case_manager_name}</TableCell>
                                  <TableCell>{record.enrollment_date}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}

              {!employmentOutcomesData && !employmentOutcomesLoading && (
                <div className="text-center py-12 text-muted-foreground">
                  <UserCheck className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg mb-2">Select a date range to generate the report</p>
                  <p className="text-sm">Choose start and end dates based on employment start dates</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Retention Report */}
        <TabsContent value="retention" className="space-y-6" data-testid="retention-report-tab">
          {!canViewSnapEtCaseload ? (
            <Card className="card-shadow">
              <CardContent className="py-12 text-center">
                <Lock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold">Access Restricted</h3>
                <p className="text-muted-foreground">This report is available to System Administrators, Executives, and Operations Managers only.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="card-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="w-5 h-5 text-purple-600" />
                      Retention Report
                    </CardTitle>
                    <CardDescription>
                      SNAP E&T participants currently or previously in retention
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/reports" className="flex items-center gap-1">
                      <ArrowLeft className="w-4 h-4" />
                      Back to Reports
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="space-y-2">
                    <Label>Retention Start Date (From)</Label>
                    <Input 
                      type="date" 
                      value={retentionFilters.start_date}
                      onChange={(e) => setRetentionFilters(prev => ({...prev, start_date: e.target.value}))}
                      data-testid="retention-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Retention Start Date (To)</Label>
                    <Input 
                      type="date" 
                      value={retentionFilters.end_date}
                      onChange={(e) => setRetentionFilters(prev => ({...prev, end_date: e.target.value}))}
                      data-testid="retention-end-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Select 
                      value={retentionFilters.states.length === 1 ? retentionFilters.states[0] : '_all'}
                      onValueChange={(v) => setRetentionFilters(prev => ({
                        ...prev, 
                        states: v === '_all' ? [] : [v]
                      }))}
                    >
                      <SelectTrigger data-testid="retention-state-filter">
                        <SelectValue placeholder="All States" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all">All States</SelectItem>
                        {retentionData?.filter_options?.states?.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                        {!retentionData?.filter_options?.states?.length && (
                          <>
                            <SelectItem value="TN">Tennessee (TN)</SelectItem>
                            <SelectItem value="NC">North Carolina (NC)</SelectItem>
                            <SelectItem value="SC">South Carolina (SC)</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end gap-2">
                    <Button 
                      onClick={fetchRetentionReport}
                      disabled={retentionLoading}
                      data-testid="generate-retention-btn"
                    >
                      {retentionLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Generate Report
                        </>
                      )}
                    </Button>
                    {retentionData && (
                      <Button 
                        variant="outline" 
                        onClick={exportRetentionReport}
                        disabled={exporting}
                        data-testid="export-retention-btn"
                      >
                        {exporting ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Export CSV
                      </Button>
                    )}
                  </div>
                </div>

                {/* Summary */}
                {retentionData && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className="text-sm text-purple-600 font-medium">Total in Retention</p>
                            <p className="text-3xl font-bold text-purple-800" data-testid="total-retention-participants">
                              {retentionData.summary?.total_retention_participants || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="bg-green-50 border-green-200">
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className="text-sm text-green-600 font-medium">Currently in Retention</p>
                            <p className="text-3xl font-bold text-green-800" data-testid="currently-in-retention">
                              {retentionData.summary?.currently_in_retention || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="bg-gray-50 border-gray-200">
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className="text-sm text-gray-600 font-medium">Previously in Retention</p>
                            <p className="text-3xl font-bold text-gray-800" data-testid="previously-in-retention">
                              {retentionData.summary?.previously_in_retention || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Data Table */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Retention Participants</CardTitle>
                        <CardDescription>
                          Click column headers to sort • {getSortedRetentionRecords().length} participants
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {retentionData.records?.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">
                            <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p>No retention participants found for the selected filters</p>
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead 
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() => sortRetentionData('participant_name')}
                                  >
                                    Participant Name
                                    {retentionSortConfig.key === 'participant_name' && (
                                      retentionSortConfig.direction === 'asc' ? 
                                        <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                        <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead 
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() => sortRetentionData('state_id_number')}
                                  >
                                    State ID Number
                                    {retentionSortConfig.key === 'state_id_number' && (
                                      retentionSortConfig.direction === 'asc' ? 
                                        <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                        <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead 
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() => sortRetentionData('program_name')}
                                  >
                                    Program Name
                                    {retentionSortConfig.key === 'program_name' && (
                                      retentionSortConfig.direction === 'asc' ? 
                                        <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                        <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead 
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() => sortRetentionData('enrollment_start_date')}
                                  >
                                    Enrollment Start
                                    {retentionSortConfig.key === 'enrollment_start_date' && (
                                      retentionSortConfig.direction === 'asc' ? 
                                        <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                        <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead 
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() => sortRetentionData('enrollment_end_date')}
                                  >
                                    Enrollment End
                                    {retentionSortConfig.key === 'enrollment_end_date' && (
                                      retentionSortConfig.direction === 'asc' ? 
                                        <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                        <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead 
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() => sortRetentionData('retention_start_date')}
                                  >
                                    Retention Start
                                    {retentionSortConfig.key === 'retention_start_date' && (
                                      retentionSortConfig.direction === 'asc' ? 
                                        <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                        <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead 
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() => sortRetentionData('retention_end_date')}
                                  >
                                    Retention End
                                    {retentionSortConfig.key === 'retention_end_date' && (
                                      retentionSortConfig.direction === 'asc' ? 
                                        <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                        <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead 
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() => sortRetentionData('case_manager_name')}
                                  >
                                    Case Manager
                                    {retentionSortConfig.key === 'case_manager_name' && (
                                      retentionSortConfig.direction === 'asc' ? 
                                        <ChevronUp className="w-4 h-4 inline ml-1" /> : 
                                        <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {getSortedRetentionRecords().map((record, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell className="font-medium">
                                      <Link to={`/cases/${record.case_id}`} className="text-purple-600 hover:underline">
                                        {record.participant_name}
                                      </Link>
                                    </TableCell>
                                    <TableCell>{record.state_id_number || '-'}</TableCell>
                                    <TableCell>
                                      <Badge variant="outline" className="border-purple-300">
                                        {record.program_name}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>{record.enrollment_start_date || '-'}</TableCell>
                                    <TableCell>{record.enrollment_end_date || '-'}</TableCell>
                                    <TableCell>{record.retention_start_date || '-'}</TableCell>
                                    <TableCell>{record.retention_end_date || '-'}</TableCell>
                                    <TableCell>{record.case_manager_name || '-'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </>
                )}

                {!retentionData && !retentionLoading && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Target className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg mb-2">Click "Generate Report" to view retention data</p>
                    <p className="text-sm">Optionally filter by date range and state</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* SNAP E&T Enrollment Summary Report */}
        <TabsContent value="enrollment-summary" className="space-y-6" data-testid="enrollment-summary-tab">
          {!canViewSnapEtCaseload ? (
            <Card className="card-shadow">
              <CardContent className="py-12 text-center">
                <Lock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold">Access Restricted</h3>
                <p className="text-muted-foreground">This report is available to System Administrators, Executives, and Operations Managers only.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="card-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-blue-600" />
                      SNAP E&T Enrollment Summary Report
                    </CardTitle>
                    <CardDescription>
                      All SNAP E&T participants with course enrollments showing first and second enrollment details
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/reports" className="flex items-center gap-1">
                      <ArrowLeft className="w-4 h-4" />
                      Back to Reports
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="space-y-2">
                    <Label>Enrollment Start Date (From)</Label>
                    <Input 
                      type="date" 
                      value={enrollmentSummaryFilters.start_date}
                      onChange={(e) => setEnrollmentSummaryFilters({
                        ...enrollmentSummaryFilters, 
                        start_date: e.target.value, 
                        current_month: false, 
                        current_fiscal_year: false
                      })}
                      data-testid="enrollment-summary-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Enrollment Start Date (To)</Label>
                    <Input 
                      type="date" 
                      value={enrollmentSummaryFilters.end_date}
                      onChange={(e) => setEnrollmentSummaryFilters({
                        ...enrollmentSummaryFilters, 
                        end_date: e.target.value, 
                        current_month: false, 
                        current_fiscal_year: false
                      })}
                      data-testid="enrollment-summary-end-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>&nbsp;</Label>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={handleEnrollmentSummaryCurrentMonth}
                      data-testid="enrollment-summary-current-month-btn"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Current Month
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Label>&nbsp;</Label>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={handleEnrollmentSummaryCurrentFiscalYear}
                      data-testid="enrollment-summary-fiscal-year-btn"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Current Fiscal Year
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Select 
                      value={enrollmentSummaryFilters.states.length === 1 ? enrollmentSummaryFilters.states[0] : '_all'}
                      onValueChange={(v) => setEnrollmentSummaryFilters(prev => ({
                        ...prev, 
                        states: v === '_all' ? [] : [v]
                      }))}
                    >
                      <SelectTrigger data-testid="enrollment-summary-state-filter">
                        <SelectValue placeholder="All States" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all">All States</SelectItem>
                        {enrollmentSummaryData?.filter_options?.states?.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                        {!enrollmentSummaryData?.filter_options?.states?.length && (
                          <>
                            <SelectItem value="TN">Tennessee (TN)</SelectItem>
                            <SelectItem value="NC">North Carolina (NC)</SelectItem>
                            <SelectItem value="SC">South Carolina (SC)</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end gap-2">
                    <Button 
                      onClick={fetchEnrollmentSummaryReport}
                      disabled={enrollmentSummaryLoading}
                      data-testid="generate-enrollment-summary-btn"
                    >
                      {enrollmentSummaryLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Generate
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Summary */}
                {enrollmentSummaryData && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className="text-sm text-blue-600 font-medium">Total Participants</p>
                            <p className="text-3xl font-bold text-blue-800" data-testid="enrollment-summary-total">
                              {enrollmentSummaryData.summary?.total_participants || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="bg-green-50 border-green-200">
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className="text-sm text-green-600 font-medium">With First Enrollment</p>
                            <p className="text-3xl font-bold text-green-800" data-testid="enrollment-summary-first">
                              {enrollmentSummaryData.summary?.with_first_enrollment || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className="text-sm text-purple-600 font-medium">With Second Enrollment</p>
                            <p className="text-3xl font-bold text-purple-800" data-testid="enrollment-summary-second">
                              {enrollmentSummaryData.summary?.with_second_enrollment || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="bg-orange-50 border-orange-200">
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className="text-sm text-orange-600 font-medium">In Retention</p>
                            <p className="text-3xl font-bold text-orange-800" data-testid="enrollment-summary-retention">
                              {enrollmentSummaryData.summary?.in_retention || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Export Buttons */}
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        onClick={handleEnrollmentSummaryExportPdf}
                        disabled={enrollmentSummaryExportingPdf}
                        data-testid="enrollment-summary-export-pdf-btn"
                      >
                        {enrollmentSummaryExportingPdf ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <FileText className="w-4 h-4 mr-2" />
                        )}
                        Export PDF
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={handleEnrollmentSummaryExportExcel}
                        disabled={enrollmentSummaryExportingExcel}
                        data-testid="enrollment-summary-export-excel-btn"
                      >
                        {enrollmentSummaryExportingExcel ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Export Excel
                      </Button>
                    </div>

                    {/* Results Table */}
                    <div className="rounded-lg border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Participant Name</TableHead>
                            <TableHead>First Course</TableHead>
                            <TableHead>First Status</TableHead>
                            <TableHead>First Progress</TableHead>
                            <TableHead>First Retention</TableHead>
                            <TableHead>Second Course</TableHead>
                            <TableHead>Second Status</TableHead>
                            <TableHead>Second Progress</TableHead>
                            <TableHead>Second Retention</TableHead>
                            <TableHead>Last Activity</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {enrollmentSummaryData.records?.length > 0 ? (
                            enrollmentSummaryData.records.map((record, index) => (
                              <TableRow key={record.case_id || index}>
                                <TableCell>
                                  <Link 
                                    to={`/cases/${record.case_id}`} 
                                    className="text-blue-600 hover:underline font-medium"
                                    data-testid={`enrollment-summary-participant-link-${index}`}
                                  >
                                    {record.participant_name || 'Unknown'}
                                  </Link>
                                </TableCell>
                                <TableCell className="text-sm">{record.first_course_name || '-'}</TableCell>
                                <TableCell>
                                  {record.first_status && (
                                    <Badge variant={
                                      record.first_status === 'completed' ? 'default' :
                                      record.first_status === 'enrolled' || record.first_status === 'in_progress' ? 'outline' :
                                      'secondary'
                                    } className={
                                      record.first_status === 'completed' ? 'bg-green-100 text-green-700' :
                                      ''
                                    }>
                                      {record.first_status}
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm font-medium">{record.first_progress || '-'}</TableCell>
                                <TableCell>
                                  {record.first_retention && (
                                    <Badge className={
                                      record.first_retention === 'In Retention' ? 'bg-purple-100 text-purple-700' :
                                      'bg-gray-100 text-gray-700'
                                    }>
                                      {record.first_retention}
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm">{record.second_course_name || '-'}</TableCell>
                                <TableCell>
                                  {record.second_status && (
                                    <Badge variant={
                                      record.second_status === 'completed' ? 'default' :
                                      record.second_status === 'enrolled' || record.second_status === 'in_progress' ? 'outline' :
                                      'secondary'
                                    } className={
                                      record.second_status === 'completed' ? 'bg-green-100 text-green-700' :
                                      ''
                                    }>
                                      {record.second_status}
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm font-medium">{record.second_progress || '-'}</TableCell>
                                <TableCell>
                                  {record.second_retention && (
                                    <Badge className={
                                      record.second_retention === 'In Retention' ? 'bg-purple-100 text-purple-700' :
                                      'bg-gray-100 text-gray-700'
                                    }>
                                      {record.second_retention}
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {record.most_recent_activity ? record.most_recent_activity.split('T')[0] : '-'}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                                No participants with course enrollments found for the selected filters.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                )}

                {!enrollmentSummaryData && !enrollmentSummaryLoading && (
                  <div className="text-center py-12 text-muted-foreground">
                    <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Click "Generate" to load the Enrollment Summary report.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Master Enrollment Metrics Report */}
        <TabsContent value="master-enrollment-metrics" className="space-y-6" data-testid="master-enrollment-metrics-tab">
          {!canViewSnapEtCaseload ? (
            <Card className="card-shadow">
              <CardContent className="py-12 text-center">
                <Lock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold">Access Restricted</h3>
                <p className="text-muted-foreground">This report is available to System Administrators, Executives, and Operations Managers only.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="card-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-purple-600" />
                      Master Enrollment Metrics
                    </CardTitle>
                    <CardDescription>
                      Comprehensive SNAP E&T enrollment data with fiscal tracking
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/reports" className="flex items-center gap-1">
                      <ArrowLeft className="w-4 h-4" />
                      Back to Reports
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Filters and Fiscal Goal */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="space-y-2">
                    <Label>Date Range (From)</Label>
                    <Input 
                      type="date" 
                      value={masterEnrollmentFilters.start_date}
                      onChange={(e) => setMasterEnrollmentFilters(prev => ({...prev, start_date: e.target.value}))}
                      data-testid="master-enrollment-start-date"
                    />
                    <p className="text-xs text-muted-foreground">Fiscal year: Oct 1 - Sep 30</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Date Range (To)</Label>
                    <Input 
                      type="date" 
                      value={masterEnrollmentFilters.end_date}
                      onChange={(e) => setMasterEnrollmentFilters(prev => ({...prev, end_date: e.target.value}))}
                      data-testid="master-enrollment-end-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Select 
                      value={masterEnrollmentFilters.states.length === 1 ? masterEnrollmentFilters.states[0] : '_all'}
                      onValueChange={(v) => setMasterEnrollmentFilters(prev => ({
                        ...prev, 
                        states: v === '_all' ? [] : [v]
                      }))}
                    >
                      <SelectTrigger data-testid="master-enrollment-state-filter">
                        <SelectValue placeholder="All States" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all">All States</SelectItem>
                        {masterEnrollmentData?.filter_options?.states?.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                        {!masterEnrollmentData?.filter_options?.states?.length && (
                          <>
                            <SelectItem value="TN">Tennessee (TN)</SelectItem>
                            <SelectItem value="NC">North Carolina (NC)</SelectItem>
                            <SelectItem value="SC">South Carolina (SC)</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Fiscal Goal</Label>
                    <Input 
                      type="number" 
                      value={currentFiscalGoal}
                      onChange={(e) => handleFiscalGoalChange(e.target.value)}
                      placeholder="Enter target enrollments"
                      data-testid="master-enrollment-fiscal-goal"
                    />
                    <p className="text-xs text-muted-foreground">Saved per state</p>
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    <Button 
                      onClick={fetchMasterEnrollmentReport}
                      disabled={masterEnrollmentLoading}
                      data-testid="generate-master-enrollment-btn"
                    >
                      {masterEnrollmentLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Generate
                        </>
                      )}
                    </Button>
                    {masterEnrollmentData && (
                      <Button 
                        variant="outline" 
                        onClick={() => exportMasterEnrollmentReport()}
                        disabled={exporting}
                        data-testid="export-master-enrollment-btn"
                      >
                        {exporting ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        CSV
                      </Button>
                    )}
                    {masterEnrollmentData && (
                      <Button 
                        variant="outline" 
                        onClick={() => exportMasterEnrollmentReport(true)}
                        disabled={exporting}
                        data-testid="export-master-enrollment-billing-btn"
                      >
                        {exporting ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Billing CSV
                      </Button>
                    )}
                  </div>
                </div>

                {/* Summary Metrics */}
                {masterEnrollmentData && (
                  <>
                    {/* Key Metrics Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className="text-sm text-purple-600 font-medium">Fiscal Goal</p>
                            <p className="text-3xl font-bold text-purple-800" data-testid="metric-fiscal-goal">
                              {masterEnrollmentData.summary?.fiscal_goal || currentFiscalGoal || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="bg-green-50 border-green-200">
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className="text-sm text-green-600 font-medium">Total Enrollments</p>
                            <p className="text-3xl font-bold text-green-800" data-testid="metric-total-enrollments">
                              {masterEnrollmentData.summary?.total_enrollments || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className={`${masterEnrollmentData.summary?.remaining_enrollments > 0 ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}>
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className={`text-sm font-medium ${masterEnrollmentData.summary?.remaining_enrollments > 0 ? 'text-orange-600' : 'text-green-600'}`}>Remaining</p>
                            <p className={`text-3xl font-bold ${masterEnrollmentData.summary?.remaining_enrollments > 0 ? 'text-orange-800' : 'text-green-800'}`} data-testid="metric-remaining">
                              {masterEnrollmentData.summary?.remaining_enrollments || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className="text-sm text-blue-600 font-medium">Total ABAWDs</p>
                            <p className="text-3xl font-bold text-blue-800" data-testid="metric-total-abawds">
                              {masterEnrollmentData.summary?.total_abawds || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="bg-indigo-50 border-indigo-200">
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className="text-sm text-indigo-600 font-medium">Total Non-ABAWDs</p>
                            <p className="text-3xl font-bold text-indigo-800" data-testid="metric-total-non-abawds">
                              {masterEnrollmentData.summary?.total_non_abawds || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className="text-sm text-purple-600 font-medium">Total Retention</p>
                            <p className="text-3xl font-bold text-purple-800" data-testid="metric-total-retention">
                              {masterEnrollmentData.summary?.total_retention || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Billing and Program Breakdown */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className="text-sm text-muted-foreground">Total Billed</p>
                            <p className="text-2xl font-bold text-green-700" data-testid="metric-total-billed">
                              {masterEnrollmentData.summary?.total_billed || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className="text-sm text-muted-foreground">Total Unbilled</p>
                            <p className="text-2xl font-bold text-red-700" data-testid="metric-total-unbilled">
                              {masterEnrollmentData.summary?.total_unbilled || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className="text-sm text-muted-foreground">Pathways to Employability</p>
                            <p className="text-2xl font-bold" data-testid="metric-total-pathways">
                              {masterEnrollmentData.summary?.total_pathways || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4">
                          <div className="text-center">
                            <p className="text-sm text-muted-foreground">The HELP Institute</p>
                            <p className="text-2xl font-bold" data-testid="metric-total-help-institute">
                              {masterEnrollmentData.summary?.total_help_institute || 0}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Exit Outcomes */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Exit Outcomes Breakdown</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                          {masterEnrollmentData.summary?.exit_outcomes && Object.entries(masterEnrollmentData.summary.exit_outcomes).map(([outcome, count]) => (
                            <div key={outcome} className="text-center p-2 bg-gray-50 rounded">
                              <p className="text-lg font-bold">{count}</p>
                              <p className="text-xs text-muted-foreground">{outcome}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Second Enrollment Exit Outcomes (secondary) */}
                    {masterEnrollmentData.summary?.second_enrollment_exit_outcomes && (
                      <div className="rounded-md border border-dashed px-4 py-3 bg-white" data-testid="second-enrollment-exit-outcomes">
                        <p className="text-xs font-medium text-muted-foreground mb-2">
                          Second Enrollment Exit Outcomes Breakdown
                          <span className="font-normal"> · not included in the totals above</span>
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
                          {Object.entries(masterEnrollmentData.summary.second_enrollment_exit_outcomes).map(([outcome, count]) => (
                            <div key={outcome} className="text-center px-2 py-1 rounded bg-gray-50/70">
                              <p className="text-sm font-semibold text-gray-600">{count}</p>
                              <p className="text-[11px] text-muted-foreground leading-tight">{outcome}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Data Table */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Enrollment Details</CardTitle>
                        <CardDescription>
                          Click column headers to sort • {getSortedMasterEnrollmentRecords().length} enrollments
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {masterEnrollmentData.records?.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">
                            <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p>No enrollments found for the selected filters</p>
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => sortMasterEnrollmentData('assigned_case_manager')}>
                                    Case Manager
                                    {masterEnrollmentSortConfig.key === 'assigned_case_manager' && (
                                      masterEnrollmentSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => sortMasterEnrollmentData('participant_name')}>
                                    Participant Name
                                    {masterEnrollmentSortConfig.key === 'participant_name' && (
                                      masterEnrollmentSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => sortMasterEnrollmentData('county')}>
                                    County
                                    {masterEnrollmentSortConfig.key === 'county' && (
                                      masterEnrollmentSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => sortMasterEnrollmentData('state_id_number')}>
                                    State ID
                                    {masterEnrollmentSortConfig.key === 'state_id_number' && (
                                      masterEnrollmentSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => sortMasterEnrollmentData('referral_start_date')}>
                                    Referral Date
                                    {masterEnrollmentSortConfig.key === 'referral_start_date' && (
                                      masterEnrollmentSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => sortMasterEnrollmentData('enrollment_date')}>
                                    Enrollment Date
                                    {masterEnrollmentSortConfig.key === 'enrollment_date' && (
                                      masterEnrollmentSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => sortMasterEnrollmentData('abawd_status')}>
                                    ABAWD
                                    {masterEnrollmentSortConfig.key === 'abawd_status' && (
                                      masterEnrollmentSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => sortMasterEnrollmentData('retention')}>
                                    Retention
                                    {masterEnrollmentSortConfig.key === 'retention' && (
                                      masterEnrollmentSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => sortMasterEnrollmentData('program_name')}>
                                    Program
                                    {masterEnrollmentSortConfig.key === 'program_name' && (
                                      masterEnrollmentSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => sortMasterEnrollmentData('program_start_date')}>
                                    Certification Start Date
                                    {masterEnrollmentSortConfig.key === 'program_start_date' && (
                                      masterEnrollmentSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => sortMasterEnrollmentData('program_end_date')}>
                                    Certification End Date
                                    {masterEnrollmentSortConfig.key === 'program_end_date' && (
                                      masterEnrollmentSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => sortMasterEnrollmentData('exit_outcome')}>
                                    Exit Outcome
                                    {masterEnrollmentSortConfig.key === 'exit_outcome' && (
                                      masterEnrollmentSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                  <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => sortMasterEnrollmentData('billing_status')}>
                                    Billing
                                    {masterEnrollmentSortConfig.key === 'billing_status' && (
                                      masterEnrollmentSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />
                                    )}
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {getSortedMasterEnrollmentRecords().map((record, idx) => (
                                  <TableRow key={idx} className={record.is_second_enrollment ? 'bg-gray-50/70' : ''} data-testid={record.is_second_enrollment ? `master-enrollment-second-row-${idx}` : undefined}>
                                    <TableCell>{record.assigned_case_manager || '-'}</TableCell>
                                    <TableCell className="font-medium">
                                      {record.case_id ? (
                                        <Link to={`/cases/${record.case_id}`} className="text-purple-600 hover:underline">
                                          {record.participant_name}
                                        </Link>
                                      ) : (
                                        record.participant_name || '-'
                                      )}
                                      {record.is_second_enrollment && (
                                        <Badge variant="outline" className="mt-1 block w-fit whitespace-nowrap text-[10px] border-green-300 text-green-700" data-testid="second-enrollment-label">Second Enrollment</Badge>
                                      )}
                                    </TableCell>
                                    <TableCell>{record.county || '-'}</TableCell>
                                    <TableCell>{record.state_id_number || '-'}</TableCell>
                                    <TableCell>{record.referral_start_date ? formatDateUtil(record.referral_start_date) : '-'}</TableCell>
                                    <TableCell>{record.enrollment_date ? formatDateUtil(record.enrollment_date) : '-'}</TableCell>
                                    <TableCell>
                                      {record.abawd_status ? (
                                        <Badge variant={record.abawd_status === 'ABAWD' ? 'destructive' : 'secondary'} className={record.abawd_status === 'ABAWD' ? 'bg-orange-500' : ''}>
                                          {record.abawd_status}
                                        </Badge>
                                      ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant={record.retention === 'Yes' ? 'default' : 'outline'} className={record.retention === 'Yes' ? 'bg-purple-600' : ''}>
                                        {record.retention || 'No'}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>{record.program_name || '-'}</TableCell>
                                    <TableCell>{record.program_start_date ? formatDateUtil(record.program_start_date) : '-'}</TableCell>
                                    <TableCell>{record.program_end_date ? formatDateUtil(record.program_end_date) : '-'}</TableCell>
                                    <TableCell>
                                      {record.exit_outcome && record.exit_outcome !== 'Currently Enrolled' ? (
                                        <Badge variant="outline">{record.exit_outcome}</Badge>
                                      ) : (
                                        <Badge variant="destructive" className="bg-green-600 hover:bg-green-600 text-white" data-testid={`master-enrollment-currently-enrolled-${idx}`}>Currently Enrolled</Badge>
                                      )}
                                      {record.exit_outcome && record.exit_outcome !== 'Currently Enrolled' && record.exit_date && (
                                        <p className="text-xs text-muted-foreground mt-1 whitespace-nowrap" data-testid={`master-enrollment-exit-date-${idx}`}>
                                          {formatDateUtil(record.exit_date)}
                                        </p>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {record.is_second_enrollment ? (
                                        <span className="text-muted-foreground" data-testid={`master-enrollment-billing-none-${idx}`}>-</span>
                                      ) : (
                                      <div className="flex items-center gap-2">
                                        <Switch
                                          checked={record.billing_status === 'Billed'}
                                          onCheckedChange={() => handleBillingStatusToggle(record.enrollment_id, record.billing_status)}
                                          className="data-[state=checked]:bg-green-600"
                                        />
                                        <span className={`text-sm font-medium ${record.billing_status === 'Billed' ? 'text-green-600' : 'text-gray-500'}`}>
                                          {record.billing_status}
                                        </span>
                                      </div>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </>
                )}

                {!masterEnrollmentData && !masterEnrollmentLoading && (
                  <div className="text-center py-12 text-muted-foreground">
                    <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg mb-2">Click "Generate" to view enrollment metrics</p>
                    <p className="text-sm">Date range defaults to current fiscal year (Oct 1 - Sep 30)</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Caseload Review Report */}
        <TabsContent value="caseload-review" className="space-y-6" data-testid="caseload-review-report-tab">
          {!canViewSnapEtCaseload ? (
            <Card className="card-shadow">
              <CardContent className="py-12 text-center">
                <Lock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold">Access Restricted</h3>
                <p className="text-muted-foreground">This report is available to System Administrators, Executives, and Operations Managers only.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Filters Card */}
              <Card className="card-shadow">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    Caseload Review Report Filters
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="space-y-2">
                      <Label>SNAP E&T Program</Label>
                      <Select 
                        value={caseloadReviewFilters.program_type} 
                        onValueChange={(v) => setCaseloadReviewFilters(prev => ({...prev, program_type: v}))}
                      >
                        <SelectTrigger data-testid="clr-program-filter">
                          <SelectValue placeholder="All Programs" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">All Programs</SelectItem>
                          {caseloadReviewFilterOptions.programs?.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Review Week</Label>
                      <Select 
                        value={caseloadReviewFilters.week_of} 
                        onValueChange={(v) => setCaseloadReviewFilters(prev => ({...prev, week_of: v, current_week: false}))}
                      >
                        <SelectTrigger data-testid="clr-week-filter">
                          <SelectValue placeholder="All Weeks" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">All Weeks</SelectItem>
                          {caseloadReviewFilterOptions.weeks?.map(w => (
                            <SelectItem key={w} value={w}>{formatDateUtil(w)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Case Manager</Label>
                      <Select 
                        value={caseloadReviewFilters.case_manager_id} 
                        onValueChange={(v) => setCaseloadReviewFilters(prev => ({...prev, case_manager_id: v}))}
                      >
                        <SelectTrigger data-testid="clr-cm-filter">
                          <SelectValue placeholder="All Case Managers" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">All Case Managers</SelectItem>
                          {caseloadReviewFilterOptions.case_managers?.map(cm => (
                            <SelectItem key={cm.id} value={cm.id}>{cm.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Review Status</Label>
                      <Select 
                        value={caseloadReviewFilters.review_status} 
                        onValueChange={(v) => setCaseloadReviewFilters(prev => ({...prev, review_status: v}))}
                      >
                        <SelectTrigger data-testid="clr-status-filter">
                          <SelectValue placeholder="All Statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">All Statuses</SelectItem>
                          <SelectItem value="Pending">Pending</SelectItem>
                          <SelectItem value="In Progress">In Progress</SelectItem>
                          <SelectItem value="Completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <Button 
                        variant={caseloadReviewFilters.current_week ? "default" : "outline"}
                        onClick={() => setCaseloadReviewFilters(prev => ({...prev, current_week: !prev.current_week, week_of: '_all'}))}
                        className="w-full"
                        data-testid="clr-current-week-btn"
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Current Week
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={() => setCaseloadReviewFilters({
                      program_type: '_all', week_of: '_all', case_manager_id: '_all', 
                      review_status: '_all', current_week: false
                    })}>
                      Clear Filters
                    </Button>
                    <Button onClick={fetchCaseloadReviewReport} disabled={caseloadReviewLoading}>
                      {caseloadReviewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Apply Filters
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Export Button */}
              {caseloadReviewData?.summary?.length > 0 && (
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={exportCaseloadReviewCsv}>
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              )}

              {/* Summary Table */}
              {caseloadReviewLoading ? (
                <Card className="card-shadow">
                  <CardContent className="py-12">
                    <div className="flex items-center justify-center">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  </CardContent>
                </Card>
              ) : caseloadReviewData?.summary?.length > 0 ? (
                <Card className="card-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="w-5 h-5" />
                      Caseload Review Summary
                    </CardTitle>
                    <CardDescription>
                      {caseloadReviewData.total_reviews} review{caseloadReviewData.total_reviews !== 1 ? 's' : ''} found. 
                      Click a row to view participant details.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10"></TableHead>
                            <TableHead>Week Of</TableHead>
                            <TableHead>Program</TableHead>
                            <TableHead>Case Manager</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-center">Total</TableHead>
                            <TableHead className="text-center">Pathways</TableHead>
                            <TableHead className="text-center">HELP Inst</TableHead>
                            <TableHead className="text-center">Active</TableHead>
                            <TableHead className="text-center">Inactive</TableHead>
                            <TableHead className="text-center">Pending</TableHead>
                            <TableHead className="text-center">Exited</TableHead>
                            <TableHead className="text-center">Reviewed</TableHead>
                            <TableHead className="text-center">Remaining</TableHead>
                            <TableHead>Completed</TableHead>
                            <TableHead className="w-12">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {caseloadReviewData.summary.map((row, idx) => (
                            <React.Fragment key={row.header_id || idx}>
                              <TableRow 
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() => toggleCaseloadReviewExpanded(row.header_id)}
                                data-testid={`clr-row-${idx}`}
                              >
                                <TableCell>
                                  {expandedCaseloadReviews[row.header_id] ? (
                                    <ChevronUp className="w-4 h-4" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4" />
                                  )}
                                </TableCell>
                                <TableCell className="font-medium">{formatDateUtil(row.week_of)}</TableCell>
                                <TableCell>{row.program_name || row.program_type}</TableCell>
                                <TableCell>{row.case_manager_name}</TableCell>
                                <TableCell>
                                  <Badge variant={
                                    row.review_status === 'Completed' ? 'success' :
                                    row.review_status === 'In Progress' ? 'default' : 'secondary'
                                  }>
                                    {row.review_status}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center font-medium">{row.total_caseload_size}</TableCell>
                                <TableCell className="text-center">{row.pathways_enrollments}</TableCell>
                                <TableCell className="text-center">{row.help_institute_enrollments}</TableCell>
                                <TableCell className="text-center text-green-600">{row.total_active}</TableCell>
                                <TableCell className="text-center text-orange-600">{row.total_inactive}</TableCell>
                                <TableCell className="text-center text-blue-600">{row.total_pending_start}</TableCell>
                                <TableCell className="text-center text-red-600">{row.total_exited_this_week}</TableCell>
                                <TableCell className="text-center">{row.participants_reviewed}</TableCell>
                                <TableCell className="text-center">{row.participants_remaining}</TableCell>
                                <TableCell className="text-xs">
                                  {row.completed_at ? (
                                    <div>
                                      <div>{formatDateUtil(row.completed_at)}</div>
                                      <div className="text-muted-foreground">{row.completed_by_name}</div>
                                    </div>
                                  ) : '-'}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2"
                                    onClick={(e) => exportCaseloadReviewPdf(row.header_id, e)}
                                    disabled={exportingCaseloadReviewPdf[row.header_id]}
                                    title="Export PDF"
                                    data-testid={`clr-pdf-btn-${idx}`}
                                  >
                                    {exportingCaseloadReviewPdf[row.header_id] ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <FileDown className="w-4 h-4" />
                                    )}
                                  </Button>
                                </TableCell>
                              </TableRow>
                              
                              {/* Expanded Participant Details */}
                              {expandedCaseloadReviews[row.header_id] && (
                                <TableRow>
                                  <TableCell colSpan={16} className="bg-gray-50 p-0">
                                    <div className="p-4">
                                      {!caseloadReviewDetails[row.header_id] ? (
                                        <div className="flex items-center justify-center py-8">
                                          <Loader2 className="w-6 h-6 animate-spin" />
                                        </div>
                                      ) : (
                                        <div className="space-y-4">
                                          <div className="flex items-center justify-between">
                                            <h4 className="font-semibold text-sm">
                                              Participant Details ({caseloadReviewDetails[row.header_id].total} participants)
                                            </h4>
                                          </div>
                                          <div className="rounded-md border bg-white overflow-x-auto max-h-96 overflow-y-auto">
                                            <Table>
                                              <TableHeader className="sticky top-0 bg-white">
                                                <TableRow>
                                                  <TableHead>State ID</TableHead>
                                                  <TableHead>Name</TableHead>
                                                  <TableHead>Program</TableHead>
                                                  <TableHead>Contact Date</TableHead>
                                                  <TableHead>Contact Outcome</TableHead>
                                                  <TableHead>Status</TableHead>
                                                  <TableHead>Timesheet</TableHead>
                                                  <TableHead>Certificate</TableHead>
                                                  <TableHead>Exit Outcome</TableHead>
                                                  <TableHead>Summary</TableHead>
                                                  <TableHead>Actions</TableHead>
                                                </TableRow>
                                              </TableHeader>
                                              <TableBody>
                                                {caseloadReviewDetails[row.header_id].participants.map((p, pIdx) => (
                                                  <TableRow key={pIdx}>
                                                    <TableCell className="font-mono text-xs">{p.state_id_number || '-'}</TableCell>
                                                    <TableCell className="whitespace-nowrap">{p.first_name} {p.last_name}</TableCell>
                                                    <TableCell className="text-xs">{p.program_name || '-'}</TableCell>
                                                    <TableCell className="text-xs text-center">
                                                      {p.case_management_summary_generated_at ? (
                                                        new Date(p.case_management_summary_generated_at).toLocaleDateString('en-US', {
                                                          month: 'short',
                                                          day: 'numeric',
                                                          year: 'numeric'
                                                        })
                                                      ) : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                      {p.contact_outcome && (
                                                        <Badge variant="outline" className="text-xs">{p.contact_outcome}</Badge>
                                                      )}
                                                    </TableCell>
                                                    <TableCell>
                                                      {p.current_status && (
                                                        <Badge variant={
                                                          p.current_status === 'Active' ? 'success' :
                                                          p.current_status === 'Inactive' ? 'secondary' : 'outline'
                                                        } className="text-xs">
                                                          {p.current_status}
                                                        </Badge>
                                                      )}
                                                    </TableCell>
                                                    <TableCell className="text-xs">{p.timesheet || '-'}</TableCell>
                                                    <TableCell className="text-xs">{p.certificate_upload || '-'}</TableCell>
                                                    <TableCell className="text-xs">{p.exit_outcome || '-'}</TableCell>
                                                    <TableCell>
                                                      {p.case_management_summary ? (
                                                        <span className="text-xs whitespace-pre-wrap">
                                                          {p.case_management_summary}
                                                        </span>
                                                      ) : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                      <div className="flex gap-1">
                                                        {p.participant_id && (
                                                          <Button variant="ghost" size="sm" asChild className="h-7 px-2">
                                                            <Link to={`/participants/${p.participant_id}`} title="View Participant">
                                                              <Eye className="w-3 h-3" />
                                                            </Link>
                                                          </Button>
                                                        )}
                                                        {p.case_id && (
                                                          <Button variant="ghost" size="sm" asChild className="h-7 px-2">
                                                            <Link to={`/cases/${p.case_id}`} title="View Case">
                                                              <FileText className="w-3 h-3" />
                                                            </Link>
                                                          </Button>
                                                        )}
                                                      </div>
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="card-shadow">
                  <CardContent className="py-12 text-center">
                    <ClipboardList className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">
                      {caseloadReviewData ? 'No caseload reviews match your filters.' : 'Click "Apply Filters" to load caseload review data.'}
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* SNAP E&T Encounters Report */}
        <TabsContent value="snap-et-encounters" className="space-y-6" data-testid="snap-et-encounters-tab">
          {!canViewSnapEtCaseload ? (
            <Card className="card-shadow">
              <CardContent className="py-12 text-center">
                <Lock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold">Access Restricted</h3>
                <p className="text-muted-foreground">This report is available to System Administrators, Executives, and Operations Managers only.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Filters Card */}
              <Card className="card-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Filter className="w-5 h-5" />
                      SNAP E&T Encounters Report
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        onClick={exportEncountersReportCsv}
                        disabled={!encountersReportData?.programs?.length}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export CSV
                      </Button>
                    </div>
                  </div>
                  <CardDescription>
                    Participant encounters from caseload reviews where status was Active
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-4">
                    <div className="space-y-2">
                      <Label>Month</Label>
                      <Input
                        type="month"
                        value={encountersReportMonth}
                        onChange={(e) => setEncountersReportMonth(e.target.value)}
                        className="w-48"
                        data-testid="encounters-month-filter"
                      />
                    </div>
                    <Button onClick={fetchEncountersReport} disabled={encountersReportLoading}>
                      {encountersReportLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Load Report
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Report Results */}
              {encountersReportLoading ? (
                <Card className="card-shadow">
                  <CardContent className="py-12">
                    <div className="flex flex-col items-center justify-center">
                      <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                      <p className="text-muted-foreground">Loading SNAP E&T Encounters Report...</p>
                    </div>
                  </CardContent>
                </Card>
              ) : encountersReportData ? (
                <Card className="card-shadow">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">
                          {encountersReportData.month_display} Encounters
                        </CardTitle>
                        <CardDescription>
                          {encountersReportData.total_participants} participants • {encountersReportData.total_encounters} total encounters
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {encountersReportData.programs?.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No Active encounters found for {encountersReportData.month_display}</p>
                      </div>
                    ) : (
                      <div className="space-y-8">
                        {encountersReportData.programs.map((program) => (
                          <div key={program.program_type} className="space-y-3">
                            {/* Program Header */}
                            <div className="flex items-center gap-2 pb-2 border-b">
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                {program.program_label}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                ({program.participant_count} participant{program.participant_count !== 1 ? 's' : ''})
                              </span>
                            </div>
                            
                            {/* Encounters Table */}
                            <div className="rounded-md border overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-muted/50">
                                    <TableHead className="font-semibold min-w-[200px]">Participant List</TableHead>
                                    <TableHead className="font-semibold min-w-[150px]">Case Manager</TableHead>
                                    <TableHead className="font-semibold min-w-[130px]">Case Status</TableHead>
                                    <TableHead className="font-semibold text-center w-28">Encounter 1</TableHead>
                                    <TableHead className="font-semibold text-center w-28">Encounter 2</TableHead>
                                    <TableHead className="font-semibold text-center w-28">Encounter 3</TableHead>
                                    <TableHead className="font-semibold text-center w-28">Encounter 4</TableHead>
                                    <TableHead className="font-semibold text-center w-28">Encounter 5</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {program.participants.map((p, idx) => (
                                    <TableRow key={p.participant_id || idx}>
                                      <TableCell className="font-medium">
                                        <Link 
                                          to={`/participants/${p.participant_id}`}
                                          className="hover:underline text-primary"
                                        >
                                          {p.full_name}
                                        </Link>
                                      </TableCell>
                                      <TableCell>{p.case_manager_name}</TableCell>
                                      <TableCell data-testid={`encounters-case-status-${p.participant_id}`}>
                                        {p.case_status === 'Closed'
                                          ? <Badge variant="secondary">Closed {p.case_closed_date}</Badge>
                                          : <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Open</Badge>}
                                      </TableCell>
                                      <TableCell className="text-center text-sm">
                                        {p.encounter_1 || <span className="text-muted-foreground">—</span>}
                                      </TableCell>
                                      <TableCell className="text-center text-sm">
                                        {p.encounter_2 || <span className="text-muted-foreground">—</span>}
                                      </TableCell>
                                      <TableCell className="text-center text-sm">
                                        {p.encounter_3 || <span className="text-muted-foreground">—</span>}
                                      </TableCell>
                                      <TableCell className="text-center text-sm">
                                        {p.encounter_4 || <span className="text-muted-foreground">—</span>}
                                      </TableCell>
                                      <TableCell className="text-center text-sm">
                                        {p.encounter_5 || <span className="text-muted-foreground">—</span>}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="card-shadow">
                  <CardContent className="py-12 text-center text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Select a month and click "Load Report" to view SNAP E&T Encounters</p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Community Service Report */}
        <TabsContent value="community-service" className="space-y-6" data-testid="community-service-report-tab">
          <Card className="card-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-600" />
                    Community Service Report
                  </CardTitle>
                  <CardDescription>
                    Track community service participants and their hours
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/reports" className="flex items-center gap-1">
                    <ArrowLeft className="w-4 h-4" />
                    Back to Reports
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="space-y-2">
                  <Label>Year <span className="text-red-500">*</span></Label>
                  <Select 
                    value={String(communityServiceFilters.year)}
                    onValueChange={(v) => setCommunityServiceFilters(prev => ({...prev, year: parseInt(v)}))}
                  >
                    <SelectTrigger data-testid="community-service-year-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(communityServiceData?.filter_options?.years || [
                        new Date().getFullYear(),
                        new Date().getFullYear() - 1,
                        new Date().getFullYear() - 2
                      ]).map(year => (
                        <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Location <span className="text-red-500">*</span></Label>
                  <Select 
                    value={communityServiceFilters.location}
                    onValueChange={(v) => setCommunityServiceFilters(prev => ({...prev, location: v}))}
                  >
                    <SelectTrigger data-testid="community-service-location-filter">
                      <SelectValue placeholder="All Locations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Locations</SelectItem>
                      {(communityServiceData?.filter_options?.locations || ['TN', 'NC']).map(loc => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button 
                    onClick={fetchCommunityServiceReport}
                    disabled={communityServiceLoading}
                    data-testid="generate-community-service-btn"
                  >
                    {communityServiceLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Generate Report
                      </>
                    )}
                  </Button>
                  {communityServiceData && (
                    <Button 
                      variant="outline" 
                      onClick={exportCommunityServiceReport}
                      disabled={exporting}
                      data-testid="export-community-service-btn"
                    >
                      {exporting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Export CSV
                    </Button>
                  )}
                </div>
              </div>

              {/* Summary Stats */}
              {communityServiceData && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-purple-50 border-purple-200">
                      <CardContent className="pt-4">
                        <div className="text-center">
                          <p className="text-sm text-purple-600 font-medium">Total Participants</p>
                          <p className="text-3xl font-bold text-purple-800" data-testid="cs-total-participants">
                            {communityServiceData.summary?.total_participants || 0}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="pt-4">
                        <div className="text-center">
                          <p className="text-sm text-blue-600 font-medium">In Progress</p>
                          <p className="text-3xl font-bold text-blue-800" data-testid="cs-in-progress">
                            {communityServiceData.summary?.in_progress || 0}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="pt-4">
                        <div className="text-center">
                          <p className="text-sm text-green-600 font-medium">Completed</p>
                          <p className="text-3xl font-bold text-green-800" data-testid="cs-completed">
                            {communityServiceData.summary?.completed || 0}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gray-50 border-gray-200">
                      <CardContent className="pt-4">
                        <div className="text-center">
                          <p className="text-sm text-gray-600 font-medium">Closed</p>
                          <p className="text-3xl font-bold text-gray-800" data-testid="cs-closed">
                            {communityServiceData.summary?.closed || 0}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Hours Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-center">
                          <p className="text-sm text-muted-foreground">Total Required Hours</p>
                          <p className="text-2xl font-bold">{communityServiceData.summary?.total_required_hours || 0}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-center">
                          <p className="text-sm text-muted-foreground">Total Completed Hours</p>
                          <p className="text-2xl font-bold text-green-600">{communityServiceData.summary?.total_completed_hours || 0}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-center">
                          <p className="text-sm text-muted-foreground">Total Remaining Hours</p>
                          <p className="text-2xl font-bold text-orange-600">{communityServiceData.summary?.total_remaining_hours || 0}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Data Table */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Community Service Participants</CardTitle>
                      <CardDescription>
                        Click column headers to sort • {getSortedCommunityServiceRows().length} participants
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead 
                                className="cursor-pointer hover:bg-muted transition-colors"
                                onClick={() => handleCommunityServiceSort('participant_name')}
                              >
                                <div className="flex items-center gap-1">
                                  Participant Name
                                  {communityServiceSortConfig.key === 'participant_name' && (
                                    communityServiceSortConfig.direction === 'asc' ? 
                                      <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                                  )}
                                </div>
                              </TableHead>
                              <TableHead 
                                className="cursor-pointer hover:bg-muted transition-colors"
                                onClick={() => handleCommunityServiceSort('service_type')}
                              >
                                <div className="flex items-center gap-1">
                                  Service Type
                                  {communityServiceSortConfig.key === 'service_type' && (
                                    communityServiceSortConfig.direction === 'asc' ? 
                                      <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                                  )}
                                </div>
                              </TableHead>
                              <TableHead 
                                className="cursor-pointer hover:bg-muted transition-colors text-right"
                                onClick={() => handleCommunityServiceSort('required_hours')}
                              >
                                <div className="flex items-center justify-end gap-1">
                                  Required Hours
                                  {communityServiceSortConfig.key === 'required_hours' && (
                                    communityServiceSortConfig.direction === 'asc' ? 
                                      <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                                  )}
                                </div>
                              </TableHead>
                              <TableHead 
                                className="cursor-pointer hover:bg-muted transition-colors text-right"
                                onClick={() => handleCommunityServiceSort('completed_hours')}
                              >
                                <div className="flex items-center justify-end gap-1">
                                  Completed Hours
                                  {communityServiceSortConfig.key === 'completed_hours' && (
                                    communityServiceSortConfig.direction === 'asc' ? 
                                      <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                                  )}
                                </div>
                              </TableHead>
                              <TableHead 
                                className="cursor-pointer hover:bg-muted transition-colors text-right"
                                onClick={() => handleCommunityServiceSort('remaining_hours')}
                              >
                                <div className="flex items-center justify-end gap-1">
                                  Remaining Hours
                                  {communityServiceSortConfig.key === 'remaining_hours' && (
                                    communityServiceSortConfig.direction === 'asc' ? 
                                      <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                                  )}
                                </div>
                              </TableHead>
                              <TableHead 
                                className="cursor-pointer hover:bg-muted transition-colors"
                                onClick={() => handleCommunityServiceSort('status')}
                              >
                                <div className="flex items-center gap-1">
                                  Status
                                  {communityServiceSortConfig.key === 'status' && (
                                    communityServiceSortConfig.direction === 'asc' ? 
                                      <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                                  )}
                                </div>
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {getSortedCommunityServiceRows().length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                  No community service participants found for the selected filters
                                </TableCell>
                              </TableRow>
                            ) : (
                              getSortedCommunityServiceRows().map((row) => (
                                <TableRow key={row.case_id} className="hover:bg-muted/50">
                                  <TableCell>
                                    <div>
                                      <Link 
                                        to={`/cases/${row.case_id}`}
                                        className="font-medium text-primary hover:underline"
                                      >
                                        {row.participant_name}
                                      </Link>
                                      <p className="text-xs text-muted-foreground">
                                        {row.enrollment_type} • {row.location}
                                      </p>
                                    </div>
                                  </TableCell>
                                  <TableCell>{row.service_type}</TableCell>
                                  <TableCell className="text-right">{row.required_hours}</TableCell>
                                  <TableCell className="text-right font-medium text-green-600">
                                    {row.completed_hours}
                                  </TableCell>
                                  <TableCell className="text-right font-medium text-orange-600">
                                    {row.remaining_hours}
                                  </TableCell>
                                  <TableCell>
                                    <Badge 
                                      variant="outline" 
                                      className={
                                        row.status === 'Completed' ? 'bg-green-100 text-green-800 border-green-300' :
                                        row.status === 'Closed' ? 'bg-gray-100 text-gray-800 border-gray-300' :
                                        'bg-blue-100 text-blue-800 border-blue-300'
                                      }
                                    >
                                      {row.status}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
              
              {!communityServiceData && !communityServiceLoading && (
                <div className="text-center py-12 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Select filters and click "Generate Report" to view community service data</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Caseload Report */}
        <TabsContent value="caseload" className="space-y-6">
          <div className="flex justify-end gap-2">
            <Button onClick={() => handleExport('caseload')} disabled={exporting}>
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export CSV
            </Button>
            <Button variant="outline" onClick={() => handleExportPdf('caseload')} disabled={exportingPdf}>
              {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Export PDF
            </Button>
          </div>
          
          {caseloadData && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Cases by Status */}
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">Cases by Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={Object.entries(caseloadData.cases_by_status).map(([name, value]) => ({ name, value }))}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {Object.keys(caseloadData.cases_by_status).map((_, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Cases by Priority */}
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">Cases by Priority</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={Object.entries(caseloadData.cases_by_priority).map(([name, count]) => ({ name, count }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#92298d" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Cases by Program */}
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">Cases by Program</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={caseloadData.cases_by_program} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="program" type="category" width={120} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#6366f1" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Cases by Staff */}
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">Top 10 Caseloads by Staff</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={caseloadData.cases_by_staff} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="staff_name" type="category" width={100} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#22c55e" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Monthly Trend */}
              <Card className="card-shadow col-span-1 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Cases Created - Monthly Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={caseloadData.monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#92298d" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Participants Report */}
        <TabsContent value="participants" className="space-y-6">
          <div className="flex justify-end gap-2">
            <Button onClick={() => handleExport('participants')} disabled={exporting}>
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export CSV
            </Button>
            <Button variant="outline" onClick={() => handleExportPdf('participants')} disabled={exportingPdf}>
              {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Export PDF
            </Button>
          </div>
          
          {participantsData && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Enrollment Status */}
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">Enrollment Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={Object.entries(participantsData.enrollment_status).map(([name, value]) => ({ name, value }))}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {Object.keys(participantsData.enrollment_status).map((_, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Enrollments by Program */}
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">Enrollments by Program</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={participantsData.enrollments_by_program} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="program" type="category" width={120} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#6366f1" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Participants by State */}
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">Participants by State</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={participantsData.participants_by_state}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="state" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#22c55e" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Monthly Trend */}
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">New Participants - Monthly</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={participantsData.monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#92298d" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Income Statistics */}
              {participantsData.income_stats && (
                <Card className="card-shadow col-span-1 lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg">Income & Employment Statistics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20">
                        <p className="text-sm text-muted-foreground">Avg Monthly Income</p>
                        <p className="text-2xl font-bold text-green-600">${participantsData.income_stats.avg_monthly_income?.toLocaleString() || 0}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                        <p className="text-sm text-muted-foreground">Avg Household Size</p>
                        <p className="text-2xl font-bold text-blue-600">{participantsData.income_stats.avg_household_size || 0}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-orange-50 dark:bg-orange-900/20">
                        <p className="text-sm text-muted-foreground">Unemployed</p>
                        <p className="text-2xl font-bold text-orange-600">{participantsData.income_stats.unemployed_count || 0}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                        <p className="text-sm text-muted-foreground">SNAP Recipients</p>
                        <p className="text-2xl font-bold text-purple-600">{participantsData.income_stats.snap_recipients || 0}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900/20">
                        <p className="text-sm text-muted-foreground">With Emergency Contact</p>
                        <p className="text-2xl font-bold">{participantsData.participants_with_emergency_contact || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Disabilities Breakdown */}
              {participantsData.disabilities_breakdown && participantsData.disabilities_breakdown.length > 0 && (
                <Card className="card-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg">Disabilities</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={participantsData.disabilities_breakdown} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="disability" type="category" width={80} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Barriers to Employment Breakdown */}
              {participantsData.barriers_breakdown && participantsData.barriers_breakdown.length > 0 && (
                <Card className="card-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg">Barriers to Employment</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={participantsData.barriers_breakdown} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="barrier" type="category" width={150} tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#f97316" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Contracts Report */}
        <TabsContent value="contracts" className="space-y-6">
          {!canViewContracts ? (
            <Card className="card-shadow">
              <CardContent className="py-12 text-center">
                <Lock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold">Access Restricted</h3>
                <p className="text-muted-foreground">This report is only available to Admin, Executive, and Operations Manager roles.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex justify-end">
                <Button onClick={() => handleExport('contracts')} disabled={exporting}>
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Export CSV
                </Button>
              </div>
              
              {contractsData && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Contract Status */}
                  <Card className="card-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">Contract Status</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Active', value: contractsData.active_contracts },
                              { name: 'Inactive', value: contractsData.inactive_contracts }
                            ]}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            dataKey="value"
                            label={({ name, value }) => `${name}: ${value}`}
                          >
                            <Cell fill="#22c55e" />
                            <Cell fill="#ef4444" />
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Cases by Contract */}
                  <Card className="card-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">Cases by Contract</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={contractsData.cases_by_contract} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="contract" type="category" width={150} tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#92298d" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Cases by Funding Source */}
                  <Card className="card-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">Cases by Funding Source</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={contractsData.cases_by_funding} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="funding_source" type="category" width={150} tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#6366f1" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Contracts by State */}
                  <Card className="card-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">Contracts by State</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {contractsData.contracts_by_state.map((item, index) => (
                          <div key={index} className="flex items-center justify-between p-3 rounded-lg border">
                            <div>
                              <p className="font-medium">{item.state}</p>
                              <p className="text-sm text-muted-foreground">
                                {item.active} active, {item.inactive} inactive
                              </p>
                            </div>
                            <Badge variant="secondary">{item.total_cases} cases</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* Audit Report */}
        <TabsContent value="audit" className="space-y-6">
          {!canViewAudit ? (
            <Card className="card-shadow">
              <CardContent className="py-12 text-center">
                <Lock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold">Access Restricted</h3>
                <p className="text-muted-foreground">Audit reports are restricted to System Administrators only.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex justify-end gap-2">
                <Button onClick={() => handleExport('audit')} disabled={exporting}>
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Export CSV
                </Button>
                <Button variant="outline" onClick={() => handleExportPdf('audit')} disabled={exportingPdf}>
                  {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                  Export PDF
                </Button>
              </div>
              
              {auditData && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Actions Summary */}
                  <Card className="card-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">Actions by Type</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={auditData.actions_summary} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="action" type="category" width={120} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#f59e0b" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Top Users */}
                  <Card className="card-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">Top Users by Activity</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={auditData.users_summary} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="user_name" type="category" width={100} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#ef4444" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Daily Trend */}
                  <Card className="card-shadow col-span-1 lg:col-span-2">
                    <CardHeader>
                      <CardTitle className="text-lg">Audit Activity - Last 30 Days</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={auditData.daily_trend}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
                          <YAxis />
                          <Tooltip />
                          <Line type="monotone" dataKey="count" stroke="#f59e0b" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Recent Audit Logs */}
                  <Card className="card-shadow col-span-1 lg:col-span-2">
                    <CardHeader>
                      <CardTitle className="text-lg">Recent Audit Logs</CardTitle>
                      <CardDescription>Last 100 audit events</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="max-h-96 overflow-y-auto space-y-2">
                        {auditData.logs.slice(0, 100).map((log, index) => (
                          <div key={index} className="p-3 rounded-lg border border-border hover:bg-muted/50">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <p className="font-medium">{log.action}</p>
                                <p className="text-sm text-muted-foreground">
                                  By {log.user_name} • Target: {log.target_id || 'N/A'}
                                </p>
                              </div>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatDateTime(log.timestamp)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Reassign Case Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-blue-500" />
              Reassign Case
            </DialogTitle>
            <DialogDescription>
              {reassigningCase && (
                <span>
                  Reassign case for <strong>{reassigningCase.participant_name}</strong> 
                  <br />
                  <span className="text-xs text-muted-foreground">
                    Currently assigned to: {reassigningCase.current_cm_name}
                  </span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Assign to Case Manager *</Label>
              <Select value={reassignTargetCM} onValueChange={setReassignTargetCM}>
                <SelectTrigger data-testid="reassign-cm-select">
                  <SelectValue placeholder="Select a Case Manager" />
                </SelectTrigger>
                <SelectContent>
                  {availableCaseManagers.map(cm => (
                    <SelectItem key={cm.id} value={cm.id}>
                      {cm.name} ({cm.role.replace(/_/g, ' ')})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Reason for Reassignment (Optional)</Label>
              <Textarea
                placeholder="Enter reason for reassignment..."
                value={reassignReason}
                onChange={(e) => setReassignReason(e.target.value)}
                rows={3}
                data-testid="reassign-reason-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setReassignDialogOpen(false)}
              disabled={reassignLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleReassignCase} 
              disabled={reassignLoading || !reassignTargetCM}
              data-testid="reassign-confirm-btn"
            >
              {reassignLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserCog className="w-4 h-4 mr-2" />
              )}
              Reassign Case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsPage;
